import type { HotspotFoldersConfig, VisitorDirectoryConfig } from '../dynamic-viewer/hotspotFolders';
import type { HazeConfig } from '../effects/haze-regions';
/**
 * StorySplat Viewer Types
 */
import type { FocusConfig } from '../effects/focus-regions';
import type { RegionShape } from '../effects/region-shape';
import type { AnnotationLayersConfig } from '../effects/annotation-layers';
import type { RegionsConfig } from '../effects/regions';
import type { AreaShape } from '../effects/area-shape';
/** Special URL value indicating "return to original splat" */
export const ORIGINAL_SPLAT_URL = "__ORIGINAL__";

export type WaypointEasingMode = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'smoothstep' | 'smootherstep';

export interface CameraControlSettings {
    cameraMovementSpeed?: number;
    cameraRotationSensitivity?: number;
    cameraDamping?: number;
    walkSpeed?: number;
    playerHeight?: number;
    /** Walk-mode player body width (collision diameter, scene units). See SceneData.playerWidth. */
    playerWidth?: number;
    doubleTapMoveSpeed?: number;
    headBobEnabled?: boolean;
    invertCameraRotation?: boolean;
}

export interface OrbitRotationLimitsConfig {
    enabled?: boolean;
    up?: number;
    down?: number;
    left?: number;
    right?: number;
}

export interface OrbitZoomLimitsConfig {
    /** Enables min/max orbit radius (zoom) limits */
    enabled?: boolean;
    /** Minimum orbit radius (closest zoom) in scene units */
    min?: number;
    /** Maximum orbit radius (farthest zoom) in scene units */
    max?: number;
}

export interface OrbitCameraSettingsConfig {
    cameraPosition: { x: number; y: number; z: number };
    pivotPoint: { x: number; y: number; z: number };
    /** Saved orbit distance from pivot to camera. Older scenes derive this from cameraPosition and pivotPoint. */
    radius?: number;
    /** Optional yaw/pitch limits in degrees, relative to the initial orbit view */
    rotationLimits?: OrbitRotationLimitsConfig;
    /** Optional orbit radius (zoom) limits in scene units */
    zoomLimits?: OrbitZoomLimitsConfig;
}

/**
 * Anchor position on a 3x3 grid for UI element layout.
 */
export type LayoutAnchor = 'top-left' | 'top-center' | 'top-right'
  | 'center-left' | 'center' | 'center-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';

/**
 * Position of a UI element relative to an anchor point.
 */
export interface ElementPosition {
    anchor: LayoutAnchor;
    offsetX: number;  // px from anchor edge (positive = inward)
    offsetY: number;
    width?: number;   // px override (undefined = natural size)
    height?: number;  // px override (undefined = natural size)
}

/**
 * Identifiers for positionable viewer UI elements.
 */
export type UIElementId =
  | 'helpButton' | 'muteButton' | 'fullscreenButton'
  | 'watermark' | 'waypointDropdown'
  | 'scrollControls' | 'waypointBanner'
  | 'progressText' | 'modeToggle' | 'exploreControls'
  | 'prevButton' | 'playButton' | 'nextButton'
  | 'hotspotPopup' | 'portalPopup'
  | 'relightingButton'
  | 'sceneMenu';

/**
 * Custom layout overrides for viewer UI elements.
 * Desktop positions apply at >= 769px, mobile at <= 768px.
 */
export interface UILayout {
    elements?: Partial<Record<UIElementId, ElementPosition>>;     // Desktop positions
    mobile?: Partial<Record<UIElementId, ElementPosition>>;       // Mobile overrides (<= 768px)
}

/**
 * Viewer theme customization - overrides hardcoded CSS values in viewerUI.ts
 * All fields optional; missing fields fall back to built-in defaults.
 */
export interface ViewerTheme {
    // Colors (hex or rgba strings)
    fontFamily?: string;
    globalTextColor?: string;
    buttonBg?: string;
    buttonHoverBg?: string;
    buttonTextColor?: string;
    popupBg?: string;
    popupTextColor?: string;
    popupCloseBtnColor?: string;
    popupLinkBtnColor?: string;
    preloaderBg?: string;
    preloaderTextColor?: string;
    infoBannerBg?: string;
    /** When set, the waypoint banner background fades to transparent toward the bottom.
     *  Value is the % down the banner where the fade begins (e.g. "50"). Undefined = solid. */
    infoBannerGradientStart?: string;
    /** Top padding of the waypoint banner in px (controls its height/reach). Default "50". */
    infoBannerPaddingTop?: string;
    dropdownBg?: string;
    watermarkBg?: string;
    helpPanelBg?: string;
    errorPopupBg?: string;
    errorTitleColor?: string;
    lazyLoadBg?: string;
    joystickBaseColor?: string;
    joystickThumbColor?: string;
    portalPopupBg?: string;
    shareButtonBg?: string;
    shareButtonHoverBg?: string;
    shareButtonIconColor?: string;
    shareModalBackdropBg?: string;
    shareModalBg?: string;
    shareModalBorderColor?: string;
    shareModalTextColor?: string;
    shareModalTitleColor?: string;
    shareModalCloseColor?: string;
    shareModalPreviewBg?: string;
    shareModalSocialButtonBg?: string;
    shareModalSocialButtonHoverBg?: string;
    shareModalSocialButtonTextColor?: string;
    shareModalActionButtonBg?: string;
    shareModalActionButtonHoverBg?: string;
    shareModalActionButtonTextColor?: string;
    shareModalPrimaryButtonBg?: string;
    shareModalPrimaryButtonHoverBg?: string;
    shareModalPrimaryButtonTextColor?: string;
    /** Text color for custom links in the scene menu dropdown (default: white) */
    sceneMenuLinkColor?: string;
    // Typography
    buttonFontSize?: string;
    buttonFontWeight?: string;
    popupTitleFontSize?: string;
    popupTitleFontWeight?: string;
    /** Hotspot popup title text */
    popupTitleFontFamily?: string;
    popupTitleLetterSpacing?: string;
    popupContentFontSize?: string;
    popupContentFontWeight?: string;
    /** Hotspot popup description text */
    popupContentFontFamily?: string;
    popupContentLetterSpacing?: string;
    popupContentLineHeight?: string;
    popupContentOpacity?: string;
    /** Hotspot popup title and description alignment. Defaults to centered. */
    popupTextAlign?: 'center' | 'left';
    /**
     * Default hotspot popup style: the centered popup, or an edge-to-edge drawer that slides
     * in from the side. A hotspot's own popupLayout / popupAnchor overrides this.
     */
    hotspotPopupLayout?: 'popup' | 'drawer';
    /** Side the theme-level drawer slides in from. Default 'right'. */
    hotspotPopupDrawerSide?: 'left' | 'right';
    infoBannerTitleFontSize?: string;
    infoBannerContentFontSize?: string;
    /** Waypoint info popup title text */
    infoBannerTitleFontFamily?: string;
    infoBannerTitleFontWeight?: string;
    infoBannerTitleLetterSpacing?: string;
    infoBannerTitleColor?: string;
    /** Waypoint info popup description text */
    infoBannerContentFontFamily?: string;
    infoBannerContentFontWeight?: string;
    infoBannerContentLetterSpacing?: string;
    infoBannerContentLineHeight?: string;
    infoBannerContentOpacity?: string;
    infoBannerContentColor?: string;
    progressFontSize?: string;
    progressFontWeight?: string;
    /** Tour progress ("45%") text. Colour falls back to globalTextColor, font to the global fontFamily. */
    progressTextColor?: string;
    progressFontFamily?: string;
    progressLetterSpacing?: string;
    modeBtnFontSize?: string;
    modeBtnFontWeight?: string;
    watermarkFontSize?: string;
    watermarkFontWeight?: string;
    /** Scene menu toggle + item text */
    sceneMenuFontSize?: string;
    sceneMenuFontWeight?: string;
    /** Waypoint dropdown toggle + list items */
    waypointListFontSize?: string;
    waypointListFontWeight?: string;
    /** Portal confirmation popup (appears when entering a portal) */
    portalPopupTitleFontSize?: string;
    portalPopupTitleFontWeight?: string;
    portalPopupBtnFontSize?: string;
    portalPopupBtnFontWeight?: string;
    /** Photo share modal text */
    shareModalFontFamily?: string;
    shareModalTitleFontSize?: string;
    shareModalTitleFontWeight?: string;
    shareModalActionFontSize?: string;
    shareModalActionFontWeight?: string;
    shareModalSocialFontSize?: string;
    shareModalSocialFontWeight?: string;
    // Border Radii
    buttonBorderRadius?: string;
    popupBorderRadius?: string;
    dropdownBorderRadius?: string;
    helpPanelBorderRadius?: string;
    errorPopupBorderRadius?: string;
    lazyLoadBtnBorderRadius?: string;
    shareButtonBorderRadius?: string;
    shareModalBorderRadius?: string;
    shareModalPreviewBorderRadius?: string;
    shareModalButtonBorderRadius?: string;
    // Other
    dropdownBlur?: string;
    progressBarHeight?: string;
    preloaderBarHeight?: string;
    shareModalBackdropBlur?: string;
    // UI Layout overrides
    uiLayout?: UILayout;
}
/**
 * Custom external link for the scene navigation menu
 */
export interface SceneMenuLink {
    id: string;
    label: string;
    url: string;
    icon?: 'link' | 'map' | 'calendar' | 'phone' | 'cart' | 'globe' | 'download';
    menuPath?: string;
    /**
     * Unified scene-menu sort key shared with portals (see PortalData.menuOrder).
     * Undefined on legacy scenes — viewer falls back to "portals first, then
     * links" order. Keep in lockstep with editor SceneMenuLink.menuOrder.
     */
    menuOrder?: number;
    openInNewTab?: boolean; // default true
    /** Content type for the menu link — 'link' opens URL, others show popup */
    contentType?: 'link' | 'pdf' | 'image' | 'video' | 'iframe';
    /** URL for media content (PDF, image, video) when contentType is not 'link' */
    mediaUrl?: string;
    /** Filename of the uploaded media */
    mediaName?: string;
    /**
     * When set to 'splat-swap', clicking this menu link swaps the displayed splat
     * in place (scene + menu stay mounted) instead of navigating. Distinct from
     * contentType so legacy link/media behavior is untouched.
     */
    swapAction?: 'splat-swap';
    /** Target splat URL to swap to when swapAction === 'splat-swap'. */
    targetSplatUrl?: string;
    /** Cutaway change a click on this item also makes (alongside its link, media or swap). See CutawayAction. */
    cutaway?: CutawayAction;
}

/**
 * "Ask this scene": a visitor-facing Q&A guide (uiOptions.sceneGuide). When enabled, published
 * scenes show an Ask button that opens a small chat panel; questions go to the StorySplat
 * sceneGuide endpoint (ViewerOptions.sceneGuideEndpoint), which answers from the scene's own
 * content. Never shown in the editor, or in scenes without a scene id.
 */
export interface SceneGuideConfig {
    enabled: boolean;
    /** Guide name shown in the panel header and the button label ("Ask <name>"). Default "Guide". */
    name?: string;
    /** First assistant bubble. Default "Ask me anything about this place." */
    greeting?: string;
    /** Up to 4 question chips shown until the visitor asks the first question. */
    suggestedQuestions?: string[];
    /**
     * Extra facts the creator wants the guide to know. Read by the backend only; the viewer
     * never displays it. Note it ships with the scene JSON like the rest of uiOptions.
     */
    knowledge?: string;
}

// Re-export from transformers
export type { ViewerConfig } from '../transformers/sceneToConfig';
// Re-export from createViewerFromSceneId
export type { ViewerFromSceneIdOptions } from '../dynamic-viewer/createViewerFromSceneId';
// Re-export frame sequence types
export type { FrameSequenceConfig, FrameSequencePlayerOptions } from '../dynamic-viewer/FrameSequencePlayer';
/**
 * HTML Mesh form field configuration
 */
export interface HtmlMeshFormField {
    type: 'text' | 'email' | 'password' | 'select' | 'checkbox' | 'radio' | 'textarea';
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    options?: {
        value: string;
        label: string;
    }[];
    defaultValue?: string;
}
/**
 * HTML Mesh form configuration
 */
export interface HtmlMeshFormConfig {
    fields: HtmlMeshFormField[];
    submitUrl?: string;
    submitMethod?: 'GET' | 'POST';
    submitButtonText?: string;
    submitButtonColor?: string;
}
/**
 * HTML Mesh configuration (renders HTML as 3D texture)
 */
export interface HtmlMeshConfig {
    /** Unique ID for the mesh */
    id: string;
    /** Content type: html, iframe, or form */
    contentType?: 'html' | 'iframe' | 'form';
    /** HTML content to render (for contentType: 'html') */
    html?: string;
    /** Raw HTML content (alias for html) */
    htmlContent?: string;
    /** CSS styles for the container */
    css?: string;
    /** iFrame URL (for contentType: 'iframe') */
    iframeUrl?: string;
    /** Form configuration (for contentType: 'form') */
    formConfig?: HtmlMeshFormConfig;
    /** Position in world space */
    position: {
        x: number;
        y: number;
        z: number;
    };
    /** Rotation in degrees */
    rotation?: {
        x: number;
        y: number;
        z: number;
    };
    /** Scale of the mesh */
    scale?: {
        x: number;
        y: number;
        z: number;
    };
    /** Width in pixels (default: 512) */
    width?: number;
    /** Height in pixels (default: 512) */
    height?: number;
    /** Whether to update the texture every frame (for animations) */
    animated?: boolean;
    /** Update rate in milliseconds (default: 100ms for animated) */
    updateRate?: number;
    /** Whether to face the camera (billboard mode) */
    billboard?: boolean;
    /** Billboard range control (percentage or waypoint based) */
    billboardRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    /** Visibility range control */
    visibilityRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    /** Whether to receive shadows */
    receiveShadows?: boolean;
    /** Whether to cast shadows */
    castShadows?: boolean;
    /** Opacity (0-1) */
    opacity?: number;
    /** Whether the mesh is double-sided */
    doubleSided?: boolean;
    /** Whether the mesh is visible */
    visible?: boolean;
    /** Background color (hex) */
    backgroundColor?: string;
    /** Padding in pixels */
    padding?: number;
    /** Border radius in pixels */
    borderRadius?: number;
    /** Font size (CSS value) */
    fontSize?: string;
    /** Font family */
    fontFamily?: string;
    /** Whether the mesh is clickable */
    clickable?: boolean;
    /** Fit strategy for content */
    fitStrategy?: 'none' | 'contain' | 'cover' | 'stretch';
    /** Whether to capture pointer on hover */
    captureOnPointerEnter?: boolean;
    /** Whether to render as canvas overlay */
    isCanvasOverlay?: boolean;
}
/**
 * Fields editorInstance.updateHtmlMesh applies to a live HTML mesh exactly as re-creating it
 * would: transform (scale is the scene scale, as at creation), pixel size, content (html /
 * css / iframe URL / content type, which replaces only that mesh's DOM element), the editor
 * visibility toggle and the visibility range. Opacity and formConfig are stored only (the
 * CSS3D element renders at full opacity; forms are not rendered by the viewer).
 */
export type HtmlMeshUpdateData = Partial<Pick<HtmlMeshConfig,
    'position' | 'rotation' | 'scale' | 'width' | 'height' | 'contentType' | 'html' | 'htmlContent' | 'css' | 'iframeUrl'
    | 'visible' | 'visibilityRange' | 'opacity' | 'formConfig'>>;
/**
 * 3D Model interaction configuration
 */
export interface CustomMeshInteraction {
    /** Activation mode - when interactions trigger */
    activationMode?: 'hover' | 'click';
    /** Whether to show UI popup */
    triggerUIPopup?: boolean;
    /** When to show popup */
    popupTriggerMode?: 'hover' | 'click';
    /** Whether to play audio */
    playAudio?: boolean;
    /** When to play audio */
    audioTriggerMode?: 'autoplay' | 'proximity' | 'hover' | 'click';
    /** How click-triggered mesh audio toggles on repeated clicks */
    audioClickBehavior?: 'pause-resume' | 'stop-restart';
    /** Stop proximity-triggered audio when leaving range. Default: true */
    audioStopOnExit?: boolean;
    /** Distance at which proximity-triggered audio activates. Separate from audioMaxDistance (spatial rolloff). */
    audioProximityRadius?: number;
    /** Whether to play model animation */
    playModelAnimation?: boolean;
    /** When to play animation */
    animationTriggerMode?: 'hover' | 'click';
    /** Whether animation should auto-play on load */
    animationAutoPlay?: boolean;
    /** Whether animation should loop */
    animationLoop?: boolean;
    /** Whether to trigger direct link */
    triggerDirectLink?: boolean;
    /** When to trigger link */
    directLinkTriggerMode?: 'hover' | 'click';
    /** URL for direct link */
    directLinkUrl?: string;
    /**
     * Actions this model's activation also runs on OTHER hotspots or models (play a video,
     * open a popup, start an animation...). They run on click, or on hover when
     * `activationMode` is 'hover', in addition to the interactions above. Absent = none.
     */
    linkedActions?: LinkedAction[];
    /** Cutaway change this model's activation also makes (click, or hover-in in hover activation). See CutawayAction. */
    cutaway?: CutawayAction;
    /** Splat this model's activation shows (click, or hover-in in hover activation). See SplatSwapAction. */
    splatSwap?: SplatSwapAction;
    /** Audio URL */
    audioUrl?: string;
    /** Loop audio */
    audioLoop?: boolean;
    /** Audio volume (0-1) */
    audioVolume?: number;
    /** Use spatial/3D audio */
    spatialSound?: boolean;
    /** Alias for spatialSound */
    audioSpatial?: boolean;
    /** Audio max distance */
    audioMaxDistance?: number;
    /** Audio reference distance for spatial audio */
    audioRefDistance?: number;
    /** Audio distance model */
    audioDistanceModel?: 'linear' | 'inverse' | 'exponential';
    /** Audio rolloff factor */
    audioRolloffFactor?: number;
    /** Popup content */
    title?: string;
    information?: string;
    informationHtml?: string;
    photoUrl?: string;
    /** Video URL for popup content (embedded video player) */
    popupVideoUrl?: string;
    iframeUrl?: string;
    externalLinkUrl?: string;
    externalLinkText?: string;
    /** External link button color */
    externalLinkButtonColor?: string;
    backgroundColor?: string;
    textColor?: string;
    /** Font size for popup */
    fontSize?: number;
    /** Font family for popup */
    fontFamily?: string;
    /** Close button color */
    closeButtonColor?: string;
}
/**
 * Custom 3D mesh configuration (GLB/GLTF models)
 */
/** Fields the editor may push to a live custom mesh via `updateCustomMesh` (transform + entity opacity). */
export type CustomMeshUpdateData = Partial<{
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
    unlit: boolean;
    /** Casts shadows while a light casts (default true). */
    castShadows: boolean;
    opacityMode: CustomMeshConfig['opacityMode'];
    opacity: CustomMeshConfig['opacity'];
    opacityAnimation: CustomMeshConfig['opacityAnimation'];
    /** Splat objects: wind-reactive (a response) or not (null / undefined). */
    wind: WindResponse | null | undefined;
}>;

export interface CustomMeshConfig {
    id?: string;
    name?: string;
    /** URL to the 3D model file (GLB/GLTF/splat/ply/sog/spz) */
    modelUrl: string;
    /** Original filename with extension (needed to detect format from blob URLs) */
    modelName?: string;
    /** Position in world space */
    position: {
        x: number;
        y: number;
        z: number;
    };
    /** Rotation in degrees */
    rotation?: {
        x: number;
        y: number;
        z: number;
    };
    /** Scale */
    scale?: {
        x: number;
        y: number;
        z: number;
    };
    /** Whether to face camera */
    billboard?: boolean;
    /** Billboard range control */
    billboardRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    /** Interaction configuration */
    interaction?: CustomMeshInteraction;
    /** Opacity mode */
    opacityMode?: 'static' | 'animated';
    /** Static opacity (0-1) */
    opacity?: number;
    /** Animated opacity settings */
    opacityAnimation?: {
        startPercent: number;
        endPercent: number;
        startOpacity: number;
        endOpacity: number;
    };
    /** Visibility range */
    visibilityRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    /** Whether the model receives shadows (default true). */
    receiveShadows?: boolean;
    /** Whether the model casts shadows while a light casts (default true). */
    castShadows?: boolean;
    /**
     * Render the model unlit: flat base colour/texture that ignores scene lights and
     * ambient (same treatment as glTF KHR_materials_unlit). Default: false — the model
     * takes the scene's lights and, when splat relighting is enabled, its ambient.
     */
    unlit?: boolean;
    /** Whether the mesh is enabled/visible */
    enabled?: boolean;
    /** Splat objects only: blow in the scene wind (trees, grass, flags...). */
    wind?: WindResponse;
}
/**
 * Light configuration
 */
export interface LightConfig {
    id?: string;
    name?: string;
    /** Light type. 'hemispheric' is deprecated — migrated to 'directional' in transformSceneToExportProps. */
    type: 'point' | 'directional' | 'spot' | 'hemispheric' | 'ambient';
    /** Position in world space */
    position?: {
        x: number;
        y: number;
        z: number;
    };
    /** Direction (for directional/spot lights) */
    direction?: {
        x: number;
        y: number;
        z: number;
    };
    /** Light color (hex string) */
    color?: string;
    /** Light intensity (0-10) */
    intensity?: number;
    /** Range for point/spot lights */
    range?: number;
    /** Inner/outer cone angle for spot lights (degrees) */
    innerConeAngle?: number;
    outerConeAngle?: number;
    /** Ground color for hemispheric lights */
    groundColor?: string;
    /** Whether the light is enabled (default: true) */
    enabled?: boolean;
    /**
     * Whether this light casts shadows (directional, spot, point) while the scene's shadows are
     * on (`splatRelighting.shadowsEnabled`). Every type shadows models and the splats (spot and
     * point lights the splats only with `splatRelighting.spotPointShadowsOnScan`, on from
     * `shadowsVersion` 3).
     */
    castShadows?: boolean;
    /**
     * Shadow map size in texels: 512, 1024, 2048 (default) or 4096 (a point light's: per cube
     * face). Directional maps are capped at 1024 on phones; spot / point maps share one texture of
     * up to 4096 (2048 on phones and in performance mode).
     */
    shadowMapSize?: number;
    /** Shadow edge softness 0 (crisp) - 1 (very soft). Default 0.35. */
    shadowSoftness?: number;
    /** Shadow bias in world units; raise it if surfaces shadow themselves (acne). Default 0.05. */
    shadowBias?: number;
    /** Normal offset bias for models receiving this light's shadow. Default 0.05. */
    normalOffsetBias?: number;
}
/**
 * Particle system configuration
 */
export interface ParticleConfig {
    id?: string;
    name?: string;
    /** Position in world space */
    position: {
        x: number;
        y: number;
        z: number;
    };
    /** Number of particles */
    numParticles?: number;
    /** Particle lifetime (seconds) */
    lifetime?: number;
    /** Emission rate (particles per second) */
    rate?: number;
    /** Emission rate variance */
    rate2?: number;
    /** Start/end speed */
    startSpeed?: number;
    endSpeed?: number;
    /** Start/end scale */
    scaleMin?: number;
    scaleMax?: number;
    /** Emitter shape */
    emitterShape?: 'box' | 'sphere' | 'point';
    /** Emitter extents */
    emitterExtents?: {
        x: number;
        y: number;
        z: number;
    };
    /** Emitter radius */
    emitterRadius?: number;
    /** Velocity */
    velocity?: {
        x: number;
        y: number;
        z: number;
    };
    /** Local velocity */
    localVelocity?: {
        x: number;
        y: number;
        z: number;
    };
    /** Color start/end (hex) */
    colorStart?: string;
    colorEnd?: string;
    /** Opacity start/end */
    opacityStart?: number;
    opacityEnd?: number;
    /** Blend mode */
    blendType?: 'additive' | 'alpha' | 'multiply';
    /** Whether to loop */
    loop?: boolean;
    /** Pre-warm duration */
    preWarm?: boolean;
    /** Sort mode */
    sort?: number;
    /** Lighting */
    lighting?: boolean;
    /** Texture URL */
    textureUrl?: string;
    /** Legacy texture selector used by older scenes */
    particleTexture?: string;
    /** Legacy custom texture URL used when particleTexture = 'custom' */
    customTextureUrl?: string;
    /** Translation pivot for particle rotation/scale */
    translationPivot?: {
        x: number;
        y: number;
    };
    /** Rendering group ID for z-ordering (0-3, higher renders on top) */
    renderingGroupId?: number;
    /** Particles drift with the scene wind while the scene has wind (rain, snow and dust presets turn it on). */
    followSceneWind?: boolean;
}
/**
 * Collision mesh configuration
 */
export interface CollisionMeshConfig {
    id?: string;
    name?: string;
    /** Mesh type */
    meshType: 'cube' | 'sphere' | 'torus' | 'plane' | 'floor' | 'custom';
    /** Position in world space */
    position: {
        x: number;
        y: number;
        z: number;
    } | number[];
    /** Rotation in degrees */
    rotation?: {
        x: number;
        y: number;
        z: number;
    } | number[];
    /** Scale/dimensions */
    scaling?: {
        x: number;
        y: number;
        z: number;
    } | number[];
    /** Custom mesh URL (for meshType: 'custom') */
    modelUrl?: string;
    /** Legacy alias for modelUrl */
    customMeshUrl?: string;
    /** Inline vertex data as flat xyz triples (for custom collision shapes without URL) */
    meshData?: number[];
    /** Visibility */
    visible?: boolean;
    /** "Inside"/containment mode (sphere): keep the player WITHIN the volume (a
     *  dome) rather than blocking entry. */
    inside?: boolean;
}
/**
 * Splat relighting configuration
 */
export interface SplatRelightingConfig {
    /** Whether splat relighting is enabled (default: true when lights exist) */
    enabled?: boolean;
    /** Ambient light color for unlighted areas (hex, default '#ffffff') */
    ambientColor?: string;
    /**
     * Ambient multiplier for relit splats (and models while relighting is on). When omitted the
     * viewer uses 1 (the scan as captured, lights add to it); the editor starts new scenes at 0
     * (only lit where a light reaches).
     */
    ambientIntensity?: number;
    /** Show toggle button in viewer UI so end users can switch relighting on/off */
    allowViewerToggle?: boolean;
    /** When allowViewerToggle is true, whether relighting starts ON or OFF for end users (default: false = off) */
    viewerDefaultOn?: boolean;
    /**
     * Shadows on (default: false). Each light then casts per its own `castShadows`. Applies
     * whether or not relighting is enabled.
     */
    shadowsEnabled?: boolean;
    /**
     * How dark shadows are, 0-1 (default 0.7; scenes without `shadowsVersion` default to 0.4).
     * With relighting off a shadow darkens the scan by this much; with it on, a shadow removes
     * this much of the shadowed light.
     */
    shadowIntensity?: number;
    /**
     * The scan casts shadows too (onto models and onto itself), from directional lights and the
     * first 2 spot / point lights. Default false. Desktop / full tier only.
     */
    splatsCastShadows?: boolean;
    /** The scan receives shadows (default true). */
    splatsReceiveShadows?: boolean;
    /**
     * Ground plane that catches shadows where the scan has no surface (default false; on for
     * scenes without `shadowsVersion` that had shadows on).
     */
    shadowGroundPlane?: boolean;
    /** Y coordinate of the ground plane (default: 0) */
    shadowGroundY?: number;
    /** Size of the ground plane (default: 50) */
    shadowPlaneScale?: number;
    /**
     * Phones: 'reduced' (default: 1024 directional maps, spot / point maps share at most 2048, the
     * scan does not cast and takes shadows from one spot / point light), 'off', or 'full'.
     */
    shadowMobile?: 'reduced' | 'off' | 'full';
    /**
     * Shadow data model. Absent = written before per-light shadows: every directional light cast
     * while `shadowsEnabled` was on. 2 = per-light shadows, spot / point lights shadow models
     * only. 3 (editors write it) = spot / point lights also shadow the splats, unless
     * `spotPointShadowsOnScan` is false.
     */
    shadowsVersion?: number;
    /**
     * Spot and point lights shadow the splats as well as models: the splats take their shadows,
     * cast into their maps (with `splatsCastShadows`) and stop their relit light. Default: true
     * from `shadowsVersion` 3, false before.
     */
    spotPointShadowsOnScan?: boolean;
}

/**
 * A segment mapping a range of audio time to a range of tour progress
 */
export interface NarrationSegmentConfig {
    id: string;
    /** Start time in the audio file (seconds) */
    audioStart: number;
    /** End time in the audio file (seconds) */
    audioEnd: number;
    /** Start of tour progress range (0-100) */
    progressStart: number;
    /** End of tour progress range (0-100) */
    progressEnd: number;
    /** Per-segment volume override (0-1, default 1) */
    volume?: number;
    label?: string;
}

/**
 * Continuous narration voiceover synced to tour progress
 */
export interface NarrationTrackConfig {
    audioUrl: string;
    audioFileName?: string;
    /** Optional instrumental background music bed played with narration */
    backgroundMusicUrl?: string;
    backgroundMusicFileName?: string;
    backgroundMusicStoragePath?: string;
    /** Background music volume 0-1, default 0.18 */
    backgroundMusicVolume?: number;
    /** Generated script text for editor review/reference */
    generatedScript?: string;
    generatedAt?: number;
    /** Total audio duration in seconds */
    duration: number;
    /** Volume 0-1, default 1 */
    volume: number;
    /** Segments mapping audio time ranges to progress ranges */
    segments: NarrationSegmentConfig[];
    /** Whether narration is enabled */
    enabled: boolean;
}

/**
 * Export props for HTML generation (kept for backward compatibility)
 */
export interface ExportProps {
    splatUrl: string;
    sogUrl?: string;
    /** LOD streaming format URL (lod-meta.json) - highest priority for loading */
    lodMetaUrl?: string;
    /** Optional mobile-only counterparts; viewer prefers them on mobile UAs. */
    mobileSplatUrl?: string;
    mobileSogUrl?: string;
    mobileLodMetaUrl?: string;
    mobileCompressedPlyUrl?: string;
    fallbackUrls?: string[];
    scale?: {
        x: number;
        y: number;
        z: number;
    };
    splatPosition?: number[] | [
        number,
        number,
        number
    ];
    splatRotation?: number[] | [
        number,
        number,
        number
    ];
    invertXScale?: boolean;
    invertYScale?: boolean;
    waypoints?: WaypointData[];
    /** Named camera views off the tour path (hotspot "Fly to extra waypoint", goToExtraWaypoint). */
    extraWaypoints?: ExtraWaypointData[];
    hotspots?: HotspotData[];
    portals?: PortalData[];
    overlays?: OverlayData[];
    skybox?: {
        url: string;
        rotation?: number;
        /** Horizon offset in degrees — reprojects the skybox so its horizon shifts up (+) or down (-) relative to the scene. */
        verticalOffset?: number;
        intensity?: number;
        enableIBL?: boolean;
    };
    customMeshes?: CustomMeshConfig[];
    htmlMeshes?: HtmlMeshConfig[];
    lights?: LightConfig[];
    particles?: ParticleConfig[];
    collisionMeshes?: CollisionMeshConfig[];
    collisionMeshesData?: CollisionMeshConfig[];
    /** Voxel octree collision (.voxel.json URL). .voxel.bin is co-located. */
    voxelCollisionUrl?: string;
    /** Triangle collision mesh (.collision.glb URL). */
    collisionMeshUrl?: string;
    /** Runtime collision system selector (default: 'voxel'). */
    collisionSystem?: 'voxel' | 'mesh';
    /** Master enable for splat-derived collision queries (default: true). When false, voxel + mesh queries are bypassed; primitive collision is unaffected. */
    splatCollisionEnabled?: boolean;
    playerHeight?: number;
    /**
     * Walk-mode player body width in scene units (metres at real-world scale): the diameter of
     * the walker's horizontal collision body (radius = width / 2). Lower it so the player fits
     * through narrow doorways. Default 0.6 (absent = default); clamped to 0.1–2.
     */
    playerWidth?: number;
    walkSpeed?: number;
    skyboxUrl?: string;
    skyboxRotation?: number;
    skyboxVerticalOffset?: number;
    templateType?: TemplateType;
    uiColor?: string;
    uiOptions?: SceneData['uiOptions'];
    defaultCameraMode?: string;
    allowedCameraModes?: string[];
    cameraMovementSpeed?: number;
    cameraRotationSensitivity?: number;
    cameraDamping?: number;
    invertCameraRotation?: boolean;
    /** Scene-level FOV in degrees (default: 60). Per-waypoint FOV takes priority during tour. */
    fov?: number;
    minClipPlane?: number;
    maxClipPlane?: number;
    /** Scene background color (hex). Applied behind the splat in the viewer. */
    backgroundColor?: string;
    includeScrollControls?: boolean;
    scrollButtonMode?: 'continuous' | 'incremental' | 'percentage' | 'waypoint';
    scrollAmount?: number;
    scrollSpeed?: number;
    transitionSpeed?: number;
    /** Easing curve for manual waypoint jumps. Autoplay and scroll remain continuous. */
    waypointEasing?: WaypointEasingMode;
    /** Tour path shape: 'smooth' spline (default) or 'linear' straight point-to-point segments. */
    pathInterpolation?: 'smooth' | 'linear';
    autoPlayEnabled?: boolean;
    /** Hold the initial autoplay until LOD streaming has settled (see SceneData.autoPlayWaitForFullDetail). */
    autoPlayWaitForFullDetail?: boolean;
    autoplaySpeed?: number;
    /** Legacy alias for autoplaySpeed (SaveFile uses camelCase autoPlaySpeed) */
    autoPlaySpeed?: number;
    loopMode?: 'loop' | 'pingpong' | 'none';
    /** AR (beta) is offered when this is on and xrMode isn't 'vr'. VR is offered in every scene. */
    includeXR?: boolean;
    xrMode?: 'ar' | 'vr' | 'both';
    /** Base URL for self-hosted 8th Wall engine files. Defaults to /cdn/8thwall on same domain. */
    eighthWallBaseUrl?: string;
    customScript?: string;
    additionalSplats?: SplatSwapPoint[];
    keepMeshesInMemory?: boolean;
    enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'>;
    initialSplatExploreMode?: 'orbit' | 'fly' | 'walk';
    audioEmitters?: AudioEmitterConfig[];
    /** Speed of auto-forward movement triggered by double-tap in explore mode (default: 1.0) */
    doubleTapMoveSpeed?: number;
    /** Whether head bob is enabled in walk mode (default: true) */
    headBobEnabled?: boolean;
    /** 4DGS frame sequence configuration */
    frameSequence?: {
        frameUrls: string[];
        fps?: number;
        loop?: boolean;
        preloadCount?: number;
        autoplay?: boolean;
        rotation?: [number, number, number];
    };
    /** Splat relighting configuration (lights affect gaussian splats) */
    splatRelighting?: SplatRelightingConfig;
    /** Reveal effect preset for splat loading animation. Use 'none' to disable. Default: 'medium' */
    revealEffect?: 'fast' | 'medium' | 'slow' | 'none';
    /** Reveal effect style: 'bloom' (fast burst with overshoot) or 'radial' (classic dot wave). Default: 'bloom' */
    revealStyle?: 'bloom' | 'radial';
    /** Swap transition type: 'scanline' (wipe) or 'dissolve' (noise cross-dissolve). Default: 'dissolve' */
    swapTransitionType?: 'scanline' | 'dissolve';
    /** Whether splat swap transitions (dissolve/wipe) are enabled. Default: true */
    enableSwapTransition?: boolean;
    /** Splat swap mode: 'chain' (sequential swap), 'compare' (interactive before/after slider), or 'menu' (no auto-trigger; swapped only from scene-menu items). Default: 'chain' */
    splatSwapMode?: 'chain' | 'compare' | 'menu';
    /** Compare mode settings (only used when splatSwapMode === 'compare') */
    compareSettings?: CompareConfig;
    /** LOD streaming display settings */
    lodSettings?: LodSettingsConfig;
    /** Mobile-specific LOD/renderer settings. Falls back to lodSettings when absent. */
    mobileLodSettings?: LodSettingsConfig;
    /** Initial orbit camera settings (position + pivot) for orbit explore mode */
    orbitCameraSettings?: OrbitCameraSettingsConfig;
    /** Initial walk mode camera settings (position + euler rotation in degrees, PlayCanvas coords) */
    walkCameraSettings?: { cameraPosition: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number } };
    /** When true, entering walk mode teleports the camera to walkCameraSettings. Default: false. */
    walkSpawnOverride?: boolean;
    /** Whether camera refocus triggers on single tap or double tap (default: 'single') */
    refocusTapMode?: 'single' | 'double';
    /** Entity keyframe animations */
    entityAnimations?: EntityAnimationConfig[];
    /** Post-processing settings */
    postProcessing?: PostProcessingConfig;
    /** Mirror reflection planes */
    mirrorPlanes?: MirrorPlaneData[];
    /** Feature flag for water surface effects. Water planes are ignored unless true. */
    enableWaterEffects?: boolean;
    /** Water surface planes for replacing broken pool / pond regions in splat scans */
    waterPlanes?: WaterPlaneData[];
    /** Procedural animated gaussian-splat cloud layers (effects/clouds). */
    cloudLayers?: CloudLayerData[];
    /** The scan's own baked sky: keep it, or replace it so clouds show at any height. */
    scanSky?: ScanSkySettings;
    /** Procedural weather effect (PlayCanvas 2.18+ splat-based) */
    weather?: WeatherConfig;
    /** The scene's wind: wind-reactive splat objects and areas, clouds that follow it (effects/wind). */
    wind?: SceneWindSettings;
    /** Audio-reactive splat shader effect (music-driven motion + color) */
    audioReactive?: AudioReactiveConfig;
    /** Segment data binary URL (.segments.bin - Uint16 per Gaussian) */
    segmentDataUrl?: string;
    /** Segment metadata JSON URL (.segments.json) */
    segmentMetaUrl?: string;
    /** Enable segment hover highlighting in viewer */
    enableSegmentHover?: boolean;
    /** Guided narration track synced to tour progress */
    narrationTrack?: NarrationTrackConfig;
    /** Background music bed that persists across portal teleports (set at the entry scene). */
    crossSceneAudio?: CrossSceneAudioConfig;
    /** AI re-skin images tied to camera poses. Editor generates them; viewer renders a palette-icon dropdown with overlay blend. */
    skins?: SceneSkinExport[];
    /** Editor-authored measurements shipped as locked defaults in the viewer. */
    measurements?: MeasurementData[];
    /** Editor-authored area measurements shipped as locked defaults in the viewer. */
    areaMeasurements?: AreaMeasurementData[];
    /** Named groups of the saved measurements (see SceneData.measurementSets). */
    measurementSets?: MeasurementSetData[];
    /** Visitors pick measurement sets in the Measure panel (historical name: they were in the Layers menu). */
    measurementSetsInLayersMenu?: boolean;
    /** Those sets start unticked (see SceneData.measurementSetsStartHidden). */
    measurementSetsStartHidden?: boolean;
    /** Coloured overlays drawn on the splat, grouped into layers visitors switch from the layers panel. */
    annotationLayers?: AnnotationLayersConfig;
    /** Regions: colour, spotlight or label parts of the scan (replaces focusRegions + annotationLayers). */
    regions?: RegionsConfig;
    /** Hotspot folders, one tree (`parentId`); hotspots and extra waypoints name theirs with `folderId`. The visitor directory lists them by folder. */
    hotspotFolders?: HotspotFoldersConfig;
    /** The visitor directory: a toolbar list of the hotspots by folder and the extra waypoints. Absent: off. */
    visitorDirectory?: VisitorDirectoryConfig;
    [key: string]: unknown;
}

/**
 * AI re-skin image tied to a specific camera pose.
 * Generated in the editor via Gemini image-to-image and persisted per scene.
 * Viewer shows a palette-icon dropdown in the top-left toolbar; clicking an
 * entry flies the camera to the captured pose and fades in the image overlay.
 */
export interface SceneSkinExport {
    id: string;
    name?: string;
    prompt: string;
    createdAt: number;
    cameraPosition: { x: number; y: number; z: number };
    cameraPivot: { x: number; y: number; z: number };
    cameraRotation?: { x: number; y: number; z: number; w: number };
    cameraFov?: number;
    canvasWidth: number;
    canvasHeight: number;
    /** Public URL or local bundle path to the generated image */
    imageUrl: string;
    thumbnailUrl?: string;
    /** When set, the viewer only surfaces this skin at the given waypoint index. Undefined = available everywhere. */
    waypointIndex?: number;
}

/**
 * LOD streaming display settings for the viewer.
 * Controls which LOD preset or custom ranges to use.
 */
export interface LodSettingsConfig {
    preset: 'auto' | 'desktop-max' | 'desktop' | 'mobile-max' | 'mobile' | 'custom';
    /** @deprecated Use lodBaseDistance + lodMultiplier instead */
    lodDistances?: [number, number, number, number, number];
    /** Base distance for first LOD transition (LOD 0→1). Default: 15 */
    lodBaseDistance?: number;
    /** Geometric multiplier between successive LOD thresholds. Default: 2 */
    lodMultiplier?: number;
    /** Min LOD range (0-5) */
    lodRangeMin?: number;
    /** Max LOD range (0-5) */
    lodRangeMax?: number;
    /** Per-device override: preset to use on mobile when main preset is 'auto' */
    mobilePreset?: 'desktop-max' | 'desktop' | 'mobile-max' | 'mobile';
    /** Per-device override: preset to use on desktop when main preset is 'auto' */
    desktopPreset?: 'desktop-max' | 'desktop' | 'mobile-max' | 'mobile';
    /** Global splat budget (0 = disabled). Caps total splats across all GSplat components. */
    splatBudget?: number;
    /**
     * Splat renderer selection (PlayCanvas 2.18+).
     * - 'auto': engine chooses (currently always resolves to 'raster-cpu')
     * - 'raster-cpu': stable WebGL/WebGPU path with CPU-side sort (default)
     * - 'raster-gpu': quad raster with GPU-side sort (WebGPU only, experimental)
     * - 'compute': full tile-based compute rasterizer (WebGPU only, up to ~3x faster, experimental)
     * Non-WebGPU devices silently fall back to raster-cpu.
     */
    renderer?: 'auto' | 'raster-cpu' | 'raster-gpu' | 'compute';
}

/**
 * Post-processing settings for the viewer
 */
/**
 * Procedural weather effect using gaussian splats (PlayCanvas 2.18+).
 */
export interface WeatherConfig {
    enabled: boolean;
    preset: 'snow' | 'rain' | 'custom';
    speed?: number;
    drift?: number;
    opacity?: number;
    color?: string;
    particleMinSize?: number;
    particleMaxSize?: number;
    elongate?: number;
    extents?: { x: number; y: number; z: number };
    density?: number;
    /** Drift sideways with the scene wind while the scene has wind (default true). */
    followSceneWind?: boolean;
}

export type AudioReactivePresetName = 'subtle' | 'club' | 'mushroom' | 'custom';

/**
 * Audio-reactive splat shader effect — the scene pulses, ripples, warps, and
 * hue-cycles in sync with the scene's audio (or the microphone). Scene-source
 * settings follow an active cross-scene ambient bed through portal transitions;
 * a destination scene can override them or explicitly disable the effect.
 */
export interface AudioReactiveConfig {
    enabled: boolean;
    /** 'scene' = music, narration, and PlayCanvas spatial audio; 'mic' = microphone. Default 'scene'. */
    source?: 'scene' | 'mic';
    /** Named starting point; sliders diverge to 'custom'. Default 'subtle'. */
    preset?: AudioReactivePresetName;
    /** 0..2 master multiplier over every effect. Default 1. */
    master?: number;
    /** 0..1 bass-driven radial "breathing" (position + splat swell). */
    pulse?: number;
    /** 0..1 mid-driven ripple amplitude propagating outward through the scene. */
    wave?: number;
    /** Ripple spatial frequency in cycles per meter. Default 0.5. */
    waveScale?: number;
    /** Ripple propagation speed. Default 4. */
    waveSpeed?: number;
    /** 0..1 level-driven spatially-coherent warp/sway. */
    wobble?: number;
    /** 0..1 treble-driven hue rotation amount. */
    hueShift?: number;
    /** Hue cycles per second at full treble. Default 0.15. */
    hueSpeed?: number;
    /** Band smoothing attack in seconds. Default 0.06. */
    attack?: number;
    /** Band smoothing release in seconds. Default 0.35. */
    release?: number;
    /** Gentle idle breathing when no audio is playing. Default true. */
    idleMotion?: boolean;
}

export interface PostProcessingConfig {
    hazeRegions?: HazeConfig;
    colorEnhance?: {
        enabled: boolean;
        shadows: number;      // -3 to 3
        highlights: number;   // -3 to 3
        vibrance: number;     // -1 to 1
        midtones: number;     // -1 to 1
        dehaze: number;       // -1 to 1
    };
    focusRegions?: FocusConfig;
    vignette?: {
        enabled: boolean;
        intensity: number;    // 0 to 1
        inner: number;        // 0 to 3
        outer: number;        // 0 to 3
        curvature: number;    // 0.01 to 10
        color: string;        // hex color
    };
    fog?: {
        enabled: boolean;
        type: 'linear' | 'exp' | 'exp2';
        color: string;        // hex color
        density: number;      // 0 to 0.1, for exp/exp2
        start: number;        // for linear
        end: number;          // for linear
    };
    bloom?: {
        enabled: boolean;
        intensity: number;    // 0 to 0.1
        blurLevel: number;    // 1 to 16
    };
    ssao?: {
        enabled: boolean;
        type: 'lighting' | 'combine';
        intensity: number;    // 0 to 1
        radius: number;       // 0 to 100
        power: number;        // 0.1 to 10
        samples: number;      // 1 to 64
        minAngle: number;     // 1 to 90
        blurEnabled: boolean;
    };
    dof?: {
        enabled: boolean;
        focusDistance: number;
        focusRange: number;
        blurRadius: number;   // 2 to 10
        blurRings: number;    // 3 to 8
        nearBlur: boolean;
        highQuality: boolean;
    };
    fringing?: {
        enabled: boolean;
        intensity: number;    // 0 to 100
    };
    grading?: {
        enabled: boolean;
        brightness: number;   // 0 to 3
        contrast: number;     // 0.5 to 1.5
        saturation: number;   // 0 to 2
        tint: string;         // hex color
    };
    taa?: {
        enabled: boolean;
        jitter: number;       // 0 to 1
    };
    toneMapping?: {
        enabled: boolean;
        type: 'linear' | 'filmic' | 'hejl' | 'aces' | 'aces2' | 'neutral';
    };
    sharpness?: {
        enabled: boolean;
        value: number;        // 0 to 1
    };
    renderScale?: {
        enabled: boolean;
        value: number;        // 0.1 to 1
    };
    exposure?: {
        enabled: boolean;
        value: number;        // 0 to 5
    };
    /** Screen-space antialiasing (FXAA). On by default. Smooths mesh edges without affecting splats. */
    antialiasing?: {
        enabled: boolean;
    };
}

/**
 * 2D footprint vertex for irregular water/mirror polygon shapes.
 * Coordinates are in the entity's local plane space (multiplied by entity scale to get world units).
 * For water planes: u maps to local X, v maps to local Z (surface faces local +Y).
 * For mirror planes: u maps to plane-right, v maps to plane-up (surface faces local +Z).
 */
export interface PlanarFootprintVertex {
    u: number;
    v: number;
}

/**
 * Mirror reflection plane data - configurable planar reflection for replacing broken mirrors in splat scans
 */
export interface MirrorPlaneData {
    id: string;
    name?: string;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
    resolution?: number;   // 0.25-1.0, texture quality (default: 0.5)
    intensity?: number;    // 0-1, reflection strength (default: 1)
    tint?: string;         // hex color for mirror tint (default: '#ffffff')
    /**
     * Optional polygon footprint for irregular-shaped mirrors.
     * When omitted, the mirror renders as a unit rectangle (scaled by `scale`).
     */
    footprint?: PlanarFootprintVertex[];
}

/**
 * Water plane data - flat animated water surface for replacing broken pool / pond regions in splat scans.
 * Supports irregular polygon shapes via `footprint`.
 */
export interface WaterPlaneData {
    id: string;
    name?: string;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
    /** Surface base color (hex). Default: '#1e6b8a' */
    color?: string;
    /** Deep-water color (hex). Tints the darker, less-reflective core of the surface. Default: '#03283a' */
    deepColor?: string;
    /** Wave-driven normal strength (0-2). Default: 1 */
    waveStrength?: number;
    /** Wave animation speed multiplier. Default: 1 */
    waveSpeed?: number;
    /** Wave scale (smaller = larger ripples). Default: 1 */
    waveScale?: number;
    /** Wave direction in degrees. Default: 0 */
    waveDirection?: number;
    /** When true, disables wave motion/normal ripples for a still pool. Default: false */
    stillWater?: boolean;
    /** Reflectivity 0-1 (fresnel reflection blend). Default: 0.5 */
    reflectivity?: number;
    /** Optional polygon footprint for irregular pool shapes (e.g. kidney, free-form). */
    footprint?: PlanarFootprintVertex[];
    /**
     * The outline: 'rectangle' (the unit plane, sized by scale), 'ellipse' (circle sized by
     * scale) or 'outline' (the footprint, drawn onto the splat in the editor). Default: 'outline'
     * when there is a footprint, else 'rectangle'. An outline without a valid footprint draws nothing.
     */
    shape?: 'rectangle' | 'ellipse' | 'outline';
    /**
     * The preset the look came from ('pool' | 'lake' | 'ocean' | 'stylized'). Fields the plane
     * leaves out come from it (otherwise from the defaults older water planes get); set fields win.
     */
    preset?: 'pool' | 'lake' | 'ocean' | 'stylized';
    /** 0-1: how far you can see into the water, relative to its size. Older planes: 0.35. */
    clarity?: number;
    /** 'planar' (default): real reflections of the scene on desktop; 'sky': sky only. Phones always use sky reflections. */
    reflections?: 'planar' | 'sky';
    /** 0-1: how strongly the waves bend what is seen under the surface. */
    refraction?: number;
    /** 0-1: dancing light pattern on what is under the water. */
    caustics?: number;
    /** 0-1: foam where the water meets the shore and on wave crests. */
    foam?: number;
    /** 0-2: sun highlight strength. Default: 1 */
    sunGlint?: number;
    /** 0-1: how softly the water fades out where it meets the shore. */
    shoreSoftness?: number;
    /** Toon shading: banded colours, hard highlights, crisp foam lines. */
    stylized?: boolean;
    /** Sun direction used when the scene has no directional light: compass heading and height in degrees. */
    sunAzimuth?: number;
    sunElevation?: number;
}

/** Cloud preset: the look a layer starts from. */
export type CloudPreset = 'cumulus' | 'overcast' | 'cirrus' | 'storm' | 'fog' | 'stylised';
/** Cloud quality: 'auto' follows the device (phones and performance mode get fewer, larger splats). */
export type CloudQuality = 'auto' | 'low' | 'medium' | 'high';

/**
 * A layer of procedural, animated gaussian-splat clouds (effects/clouds). Generated in the viewer
 * from a seed (nothing to upload), so every visitor sees the same sky. Look fields the layer leaves
 * out come from its preset.
 */
export interface CloudLayerData {
    id: string;
    name?: string;
    /** Drawn when not false. */
    enabled?: boolean;
    /** Default 'cumulus'. */
    preset?: CloudPreset;
    /**
     * 'box' (default): clouds fill the box (position = centre, scale = width x thickness x depth).
     * 'dome': a sky layer around the scene: a disc of diameter scale.x and thickness scale.y whose
     * far edge sinks toward the horizon.
     */
    area?: 'box' | 'dome';
    position: { x: number; y: number; z: number };
    /** Euler radians (editor convention). Clouds stay level: only the yaw (y) is used. */
    rotation: { x: number; y: number; z: number };
    /** Box size in scene units (dome: x = diameter, y = thickness). */
    scale: { x: number; y: number; z: number };
    /** 0-1: share of the sky covered. */
    coverage?: number;
    /** 0-1: how thick (opaque) the clouds are. */
    density?: number;
    /** Typical cloud width in scene units. */
    cloudScale?: number;
    /** Degrees, the direction the clouds travel in scene XZ (0 = +X, 90 = +Z). */
    windDirection?: number;
    /** Scene units per second. */
    windSpeed?: number;
    /** 0-1: how fast the clouds billow and change shape. */
    evolution?: number;
    /** Clouds slowly dissolve and re-form. */
    lifecycle?: boolean;
    /** Sunlit colour (hex). */
    color?: string;
    /** Colour of the shaded undersides (hex). */
    shadeColor?: string;
    /** 0-1: larger, fainter splats (softer edges). */
    softness?: number;
    /** Integer seed: the same seed gives the same clouds. */
    seed?: number;
    quality?: CloudQuality;
    /** Soft moving shadows on the scan. */
    castShadows?: boolean;
    /** 0-1 */
    shadowStrength?: number;
    /** 0-1 master fade (keyframeable). Default 1. */
    opacity?: number;
    /**
     * Drift with the scene wind (its direction, and its speed times windSpeedMultiplier) instead
     * of this layer's own windDirection / windSpeed, while the scene has wind. New layers turn it
     * on; layers saved before scene wind existed leave it out and keep their own wind.
     */
    followSceneWind?: boolean;
    /** Cloud speed relative to the scene wind (high cirrus moves faster). Default from the preset. */
    windSpeedMultiplier?: number;
}


/**
 * How a part of the scene reacts to the scene wind (effects/wind): a profile with four
 * creator-tunable values. Fields left out come from the profile, so a scene keeps its motion
 * if a profile default is tuned later only for fields the creator never touched.
 */
export type WindProfile = 'trees' | 'grass' | 'bushes' | 'flags' | 'custom';
export interface WindResponse {
    /** Moves in the wind unless false. */
    enabled?: boolean;
    /** Default 'trees'. */
    profile?: WindProfile;
    /** 0-3: how far it moves (1 = the profile's motion). */
    amount?: number;
    /** 0-1: how much it resists bending; stiffer parts bend less and sway faster. */
    stiffness?: number;
    /** 0-1: small fast motion of each splat on top of the sway (leaves, fabric). */
    flutter?: number;
    /**
     * 0-0.9: the share of the part's height (flags: of its length from the upwind edge) that stays
     * planted, like a trunk.
     */
    anchor?: number;
}

/**
 * A wind-reactive area of the scan: the shared region shape picker (sphere, box or outline; see
 * effects/area-shape.ts) plus a wind response. Areas move every splat inside them; the area's
 * bottom is the anchor height, its top the crown. `apply` is always 'inside'.
 */
export interface WindArea extends AreaShape, WindResponse {
    id: string;
    name?: string;
}

/**
 * The scene's wind (scene field `wind`): one wind for everything that reacts to it, the splat
 * objects and areas marked wind-reactive, cloud layers that follow it, and weather that drifts.
 */
export interface SceneWindSettings {
    /** The wind blows unless false. */
    enabled?: boolean;
    /** Degrees, the direction the wind blows toward in scene XZ (0 = +X, 90 = +Z), as cloud layers. Default 20. */
    direction?: number;
    /** Scene units per second (the speed clouds that follow the wind drift at). Default 3. */
    speed?: number;
    /** 0-1: how much the wind comes in gusts that travel across the scene. Default 0.5. */
    gustiness?: number;
    /** 0-1: small, chaotic changes of direction and strength. Default 0.3. */
    turbulence?: number;
    /** Wind-reactive areas of the scan (up to MAX_WIND_AREAS draw). */
    areas?: WindArea[];
}

/**
 * The scan's own baked sky (effects/clouds/scanSky.ts). Outdoor scans bake the sky into a shell of
 * splats near the capture point; clouds at a believable height sit behind it. 'replace' fades
 * those sky splats (above the horizon, beyond the objects around the capture point, sparse,
 * sky-coloured) so clouds show at any height in front of the skybox or a procedural sky.
 */
export interface ScanSkySettings {
    /** 'keep' (default): the scan's sky stays and new cloud layers fit inside it; 'replace': it fades out. */
    mode?: 'keep' | 'replace';
    /** 0-1: how much of the scan's sky is removed in 'replace' mode. Default 1. */
    strength?: number;
    /** Degrees above the horizon (seen from the capture point) where the sky starts. Default 4. */
    horizon?: number;
    /** Scene units around the capture point that are always kept. Default: measured from the scan. */
    keepRadius?: number | null;
    /** The capture point (scene coordinates). Default: the start camera. */
    center?: { x: number; y: number; z: number } | null;
    /** Shown where the sky was when the scene has no skybox: a procedural sky preset, or 'none' (background colour). */
    backdrop?: 'clear' | 'hazy' | 'golden' | 'overcast' | 'none';
}

/**
 * Template type for HTML generation
 */
export type TemplateType = 'standard' | 'minimal' | 'pro';
/**
 * Cross-scene ambient audio: a background music bed set at an "entry" scene that persists across
 * portal teleports (played by the module-scope crossSceneAudio singleton, which survives viewer
 * recreation). A later scene may override it with a different url; scenes that omit it keep the
 * current bed playing.
 */
export interface CrossSceneAudioConfig {
    /** When true, loading this scene starts/keeps the persistent background bed. */
    enabled?: boolean;
    /** Audio file URL. */
    url?: string;
    /** 0..1, default 1. */
    volume?: number;
    /** default true. */
    loop?: boolean;
    /** Crossfade when a later scene overrides the bed with a different track. Default true. */
    fade?: boolean;
}
/** Snapshot of the scheduler's decisions. */
export interface OnDemandRenderStats {
    decided: number;
    rendered: number;
    skipped: number;
    heartbeats: number;
    lastReason: string;
    /** Render count per reason (capped set of keys). */
    reasons: Record<string, number>;
}

/** See ViewerInstance.getShadowDebugInfo. */
export interface ViewerShadowDebugInfo {
    tier: string;
    enabled: boolean;
    legacy: boolean;
    strength: number;
    splatsCasting: boolean;
    splatsReceive: boolean;
    groundPlane: boolean;
    shadowMapRenders: number;
    lights: Array<{
        name: string;
        type?: string;
        castShadows: boolean;
        resolution?: number;
        shadowType?: number;
        fitHalfSize: number | null;
        rendered: boolean;
        splatSlot: number;
    }>;
}

export interface ViewerOnDemandRenderStats extends OnDemandRenderStats {
    active: boolean;
    enabled: boolean;
    continuousReasons: string[];
}

/**
 * Scene data format - matches StorySplat Firebase scene structure
 */
/** Screen position of an overlay panel. Narrow screens collapse every anchor to a sheet under the toolbar. */
export type OverlayAnchor =
    | 'top-left' | 'top-center' | 'top-right'
    | 'center-left' | 'center' | 'center-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';

/** What makes an overlay appear. Conditions re-evaluate every frame; events latch it on. */
export type OverlayTrigger =
    | {
        /** Show while the camera is inside a scene-anchored box or sphere. */
        type: 'region';
        shape?: 'sphere' | 'box';
        /** Editor scene coordinates, like focus and haze regions. */
        position?: { x: number; y: number; z: number };
        rotation?: { x: number; y: number; z: number };
        size?: { x: number; y: number; z: number };
        feather?: number;
        /** Default true. False keeps the overlay up after leaving until it is dismissed. */
        hideOnExit?: boolean;
    }
    | {
        /** Show while the tour is at this waypoint index. */
        type: 'waypoint';
        index: number;
        /** Default true. False keeps the overlay up after moving on until it is dismissed. */
        hideOnLeave?: boolean;
    }
    | {
        /** Show while tour progress is inside start..end, in percent. */
        type: 'progress';
        start: number;
        end: number;
    }
    | {
        /**
         * Show while the tour is between two waypoints (0-based indices), measured along the
         * path: from the moment it reaches `from` until it moves on past `to`. In a looping
         * tour a `from` after `to` runs through the last-to-first stretch. Viewers before
         * 2.11.0 never show it.
         */
        type: 'betweenWaypoints';
        from: number;
        to: number;
    }
    | {
        /** Show when the hotspot with this id is clicked. */
        type: 'hotspot';
        hotspotId: string;
        /**
         * What a click does: 'on' shows the item (it stays until dismissed or switched off),
         * 'off' hides it, 'toggle' flips it. Unset: 'toggle' for focus / haze regions and
         * annotation layers, 'on' for overlays. Viewers before 2.11.0 always use that default.
         */
        action?: HotspotTriggerAction;
    }
    | {
        /** Show once the scene has loaded, after an optional delay. */
        type: 'sceneStart';
        delayMs?: number;
    }
    | {
        /** Only shown through viewer.showOverlay() (custom scripts, embedders). */
        type: 'manual';
    };

/** What a hotspot-click trigger does to its item. */
export type HotspotTriggerAction = 'toggle' | 'on' | 'off';

/** The same trigger kinds drive overlays and triggered focus / haze regions. */
export type SceneTrigger = OverlayTrigger;
/** A "camera enters an area" trigger: a box or sphere in editor scene coordinates. */
export type RegionTrigger = Extract<OverlayTrigger, { type: 'region' }>;

/**
 * A floating information panel drawn over the scene. Unlike a hotspot popup it is
 * not attached to a marker: its triggers decide when it appears.
 */
export interface OverlayData {
    /** Optional formatting; text/subtitle remain plain-text fallbacks for older viewers. */
    textHtml?: string;
    subtitleHtml?: string;
    id: string;
    name?: string;
    /** Default true. */
    enabled?: boolean;
    title?: string;
    /** Plain text; line breaks are preserved. */
    text?: string;
    imageUrl?: string;
    imageAlt?: string;
    videoUrl?: string;
    videoAutoplay?: boolean;
    videoLoop?: boolean;
    videoMuted?: boolean;
    linkUrl?: string;
    linkText?: string;
    /**
     * Rich content: the same blocks as hotspot popups (text, image galleries, videos,
     * links, PDFs, iframes, 3D models), rendered in order below the title. When present
     * and non-empty it replaces the single text / image / video / link fields above.
     */
    contentBlocks?: HotspotContentBlock[];
    /**
     * 'drawer' (default): an edge-to-edge column that slides in from the left or right edge
     * of the viewer, like the sidebar scene menu; `anchor` picks the side (a *-left anchor
     * docks left, anything else docks right). 'floating': a panel at `anchor`. In embeds
     * 600px wide or narrower a drawer renders as a bottom sheet and a floating panel as a
     * sheet under the toolbar, clear of the tour controls and the walk joystick.
     * 'top': the top banner a waypoint's name and info show in (full width, the theme's
     * info-banner gradient and typography), with `title` and `subtitle` only; content blocks,
     * anchor, width and dismissible don't apply, and it doesn't capture clicks. While one is
     * up, the waypoint banner yields to it. Viewers before 2.11.0 draw it as a drawer.
     */
    layout?: 'floating' | 'drawer' | 'top';
    /** The line under the title in the 'top' layout (a waypoint's info). */
    subtitle?: string;
    /** Default 'bottom-left'. */
    anchor?: OverlayAnchor;
    /** Panel width in CSS pixels for both layouts. Default 360, always clamped to the viewer. */
    width?: number;
    /**
     * Whether visitors can close it: the close button, Escape, and on phones the swipe
     * handle and (drawers) the tap-outside scrim. Default true. False leaves only the
     * trigger's own end condition, auto-hide, or the API to hide it.
     */
    dismissible?: boolean;
    /** Hide automatically this many milliseconds after showing. */
    autoHideMs?: number;
    triggers: OverlayTrigger[];
}

export interface SceneData {
    // Scene metadata
    name?: string;
    /** Creator-facing scene title (SaveFile field). Preferred over `name` for display. */
    sceneTitle?: string;
    sceneId?: string;
    userId?: string;
    userName?: string;
    thumbnailUrl?: string;
    // Splat configuration
    loadedModelUrl?: string; // Firebase format (original file - may be .splat, .ply, etc.)
    splatUrl?: string; // Direct format
    sogModelUrl?: string; // SOG URL (optimized format)
    sogUrl?: string; // Direct SOG format
    compressedPlyUrl?: string; // Compressed PLY URL (PlayCanvas preferred format)
    /** LOD streaming format URL (lod-meta.json) - highest priority for loading */
    lodMetaUrl?: string;
    /**
     * Optional mobile-only counterparts. When present, the viewer prefers them on mobile UAs
     * (or when forceMobilePreview is set). Each falls back to its desktop counterpart when absent.
     */
    mobileLoadedModelUrl?: string;
    mobileSplatUrl?: string;
    mobileSogModelUrl?: string;
    mobileSogUrl?: string;
    mobileCompressedPlyUrl?: string;
    mobileLodMetaUrl?: string;
    fallbackUrls?: string[];
    scale?: {
        x: number;
        y: number;
        z: number;
    };
    splatScale?: number; // Uniform scale factor for the splat
    position?: number[]; // [x, y, z]
    splatPosition?: [
        number,
        number,
        number
    ]; // Alternative position format
    rotation?: number[]; // [x, y, z]
    splatRotation?: [
        number,
        number,
        number
    ]; // Alternative rotation format
    invertXScale?: boolean;
    invertYScale?: boolean;
    // Scene elements
    waypoints?: WaypointData[];
    /** Named camera views off the tour path (hotspot "Fly to extra waypoint", goToExtraWaypoint). */
    extraWaypoints?: ExtraWaypointData[];
    hotspots?: HotspotData[];
    portals?: PortalData[]; // Portal markers linking to other scenes (V2 viewer only)
    /** Floating information panels shown by spatial, tour, hotspot, or manual triggers. */
    overlays?: OverlayData[];
    skybox?: {
        url: string;
        rotation?: number;
        /** Horizon offset in degrees — reprojects the skybox so its horizon shifts up (+) or down (-) relative to the scene. */
        verticalOffset?: number;
        intensity?: number;
        enableIBL?: boolean;
    };
    /**
     * Legacy skybox fields from the Babylon-based editor.
     * Prefer `skybox`, but keep these for backwards compatibility with existing stored scene JSON.
     */
    activeSkyboxUrl?: string | null;
    /** Legacy rotation in radians around the Y axis. Prefer `skybox.rotation`. */
    skyboxRotation?: number;
    /** Legacy flat skybox URL. Prefer `skybox.url` or `activeSkyboxUrl`. */
    skyboxUrl?: string;
    /** Flat horizon offset in degrees (editor save format). Prefer `skybox.verticalOffset`. */
    skyboxVerticalOffset?: number;
    customMeshes?: CustomMeshConfig[];
    htmlMeshes?: HtmlMeshConfig[];
    lights?: LightConfig[];
    particles?: ParticleConfig[];
    /** Firestore field name for particle systems (legacy scenes use this) */
    particleSystems?: ParticleConfig[];
    collisionMeshesData?: CollisionMeshConfig[];
    /** Voxel octree collision (.voxel.json URL). .voxel.bin is co-located. */
    voxelCollisionUrl?: string;
    /** Triangle collision mesh (.collision.glb URL). */
    collisionMeshUrl?: string;
    /** Runtime collision system selector (default: 'voxel'). */
    collisionSystem?: 'voxel' | 'mesh';
    /** Master enable for splat-derived collision queries (default: true). When false, voxel + mesh queries are bypassed; primitive collision is unaffected. */
    splatCollisionEnabled?: boolean;
    playerHeight?: number;
    /**
     * Walk-mode player body width in scene units (metres at real-world scale): the diameter of
     * the walker's horizontal collision body (radius = width / 2). Lower it so the player fits
     * through narrow doorways. Default 0.6 (absent = default); clamped to 0.1–2.
     */
    playerWidth?: number;
    walkSpeed?: number;
    // UI options
    uiColor?: string;
    uiOptions?: {
        showStartExperience?: boolean;
        showWatermark?: boolean;
        hideFullscreenButton?: boolean;
        hideInfoButton?: boolean;
        hideMuteButton?: boolean;
        hideHelpButton?: boolean;
        /** Hide the navigator/camera mode toggle button */
        hideNavigator?: boolean;
        /** Show dropdown list of waypoints for quick navigation */
        showWaypointList?: boolean;
        /** Hide the share button */
        hideShareButton?: boolean;
        /**
         * Capture the share photo without hotspots, portals, leader lines, or the
         * walk-mode reticle. Interface chrome is DOM and never appears in the image.
         * Default false: the photo shows the scene exactly as the visitor sees it.
         */
        sharePhotoHideEntities?: boolean;
        /** Hide the scene navigation menu button entirely (even when portals/links exist) */
        hideSceneMenu?: boolean;
        /** Show a fisheye / tiny-planet toggle button in the viewer toolbar (PlayCanvas 2.18+). */
        showFisheyeButton?: boolean;
        /**
         * Show the cutaway toolbar button. Visitors drag a vertical slider to hide the
         * splats above (or below) a horizontal plane, e.g. to look down into a building.
         */
        cutawayEnabled?: boolean;
        /** Which side of the cutaway plane disappears. Default 'above'. */
        cutawayDirection?: 'above' | 'below';
        /**
         * While the cutaway is on, switch off splat and mesh collision so the camera can rise
         * above a sliced building for a top-down view. Turning the cutaway on also leaves walk
         * mode for free explore. Default true.
         */
        cutawayDisablesCollision?: boolean;
        /**
         * Allow stereo 3D for this scene (default true). Hosts switch it on through `setStereoMode`
         * (the Discover scene page's 3D View control); `showStereoButton` adds one to the viewer. Visitors
         * pick a format for their display, red/cyan glasses on any screen, side-by-side or
         * top-and-bottom for 3D TVs and projectors, or a full side-by-side pair. `false` turns it
         * off: `setStereoMode` is ignored and `isStereoAvailable()` reports false.
         */
        stereoEnabled?: boolean;
        /**
         * Also show a 3D view button in the viewer's own toolbar (default false), for embeds and
         * exports that have no Discover page around them. Ignored when `stereoEnabled` is false.
         */
        showStereoButton?: boolean;
        /** Stereo 3D strength, 0 to 2. 1 (default) follows the 1/30 rule; 0 is flat. */
        stereoDepth?: number;
        /**
         * Distance from the camera, in scene units, that appears at the screen surface (zero
         * parallax). Nearer things come out of the screen. Absent or 0 = automatic: the orbit
         * focus while orbiting, otherwise 2.5 m in calibrated scenes or the saved orbit distance.
         */
        stereoScreenDistance?: number;
        /**
         * Hide the Photo button. By default a camera button next to Share opens a clean photo
         * mode: interface, hotspots, portals, leader lines and the reticle are hidden while the
         * visitor frames the view, and "Take photo" downloads the image.
         */
        hidePhotoButton?: boolean;
        /**
         * Protect uploaded media: no Download item in video players, no picture-in-picture, no
         * right-click / drag / long-press saving of photos, videos and PDF pages, and PDFs drawn by
         * the viewer's own renderer (no download or print buttons). Applies to hotspot popups,
         * lightboxes, overlays and menu media. A deterrent: screenshots and the browser's network
         * tools still work. Default false.
         */
        protectMedia?: boolean;
        hideWatermark?: boolean;
        watermarkText?: string;
        watermarkLink?: string;
        /** Custom watermark image URL (PNG logo) for whitelabeling - used instead of text when set */
        watermarkImageUrl?: string;
        /** Max size (width & height) of the watermark image in pixels (default 100) */
        watermarkImageHeight?: number;
        /** Show the current scene's title/name as a pill in the bottom-left corner (matches watermark style). */
        showSceneTitle?: boolean;
        /** Sub-option of showSceneTitle: only reveal the bottom-left title on hover. Default false = always shown. */
        sceneTitleShowOnHover?: boolean;
        /** Initial movement-key family shown in the desktop Explore controls. */
        movementKeyDisplay?: 'wasd' | 'arrows';
        /** When true, skips WebGPU device creation and initializes with WebGL2. */
        disableWebGPU?: boolean;
        buttonPosition?: 'inline' | 'below';
        /** Custom button labels for internationalization (i18n) */
        buttonLabels?: ViewerButtonLabels;
        /** Custom preloader logo URL for whitelabeling */
        customPreloaderLogoUrl?: string;
        /** Preloader logo height in pixels (default 64 desktop, 40 mobile). */
        preloaderLogoSize?: number;
        /** Enable lazy loading - shows thumbnail with start button before loading */
        lazyLoad?: boolean;
        /** Custom button text for lazy loading. Default: "Start Experience" */
        lazyLoadButtonText?: string;
        /** Custom thumbnail URL for lazy loading screen background */
        lazyLoadThumbnailUrl?: string;
        /** Type of media for the lazy load thumbnail: 'image' | 'video' | 'gif' */
        lazyLoadThumbnailType?: 'image' | 'video' | 'gif';
        /** UI layout type: 'standard' (centered), 'minimal', or 'pro' (dark) */
        uiType?: 'standard' | 'minimal' | 'pro';
        /** Enable debug mode (FPS counter) */
        debugMode?: boolean;
        /** Hide the progress percentage text */
        hideProgressText?: boolean;
        /** Custom viewer theme overrides for CSS styling */
        viewerTheme?: ViewerTheme;
        /** Custom external links shown in the scene menu alongside portals */
        sceneMenuLinks?: SceneMenuLink[];
        /** Layout style for the scene menu: 'classic' (default dropdown) or 'sidebar' (full-height left pullout) */
        sceneMenuStyle?: 'classic' | 'sidebar';
        /** When true, scene menu category dropdowns (folders) start collapsed. Default: expanded. */
        startCategoriesCollapsed?: boolean;
        /** Show a preview image next to each connected scene in the menu. Default false (off). */
        showSceneMenuPreviewImages?: boolean;
        /** Persisted menu slot for the synthesized "Current Space" item — its folder (path) and a
         *  stable order so the current scene doesn't drift between scenes. Order defaults to top. */
        currentSpaceMenuPath?: string;
        currentSpaceMenuOrder?: number;
        /** Explicit render order of menu folders by full path; unlisted folders keep insertion order. */
        sceneMenuFolderOrder?: string[];
        /** Optional text title shown at the top of the scene menu (both classic and sidebar modes). Ignored when menuTitleImageUrl is set. */
        menuTitle?: string;
        /** Optional logo image URL shown at the top of the scene menu instead of menuTitle text. */
        menuTitleImageUrl?: string;
        /** When true, preload hotspot popup images (photoUrl + image content blocks) at scene load so
         *  the first popup opens instantly. Off by default — costs bandwidth/memory up front. */
        preloadHotspotImages?: boolean;
        /**
         * When true (default), the viewer pauses its render loop and suspends audio
         * when the tab/page is hidden. Set false to keep the scene running while
         * backgrounded (kiosks, monitoring displays).
         */
        pauseOnHide?: boolean;
        /** Whether typing the `fpv` keyboard sequence can enter FPV drone mode. Default: true. */
        fpvKeyboardShortcutEnabled?: boolean;
        /** "Ask this scene" visitor Q&A guide (Ask button + chat panel). Off unless enabled is true. */
        sceneGuide?: SceneGuideConfig;
        /** Performance metrics HUD (F3). Enabled by default; set false to disable for this scene. */
        perfHudEnabled?: boolean;
        /**
         * Start phone and tablet visitors in performance mode (half render-resolution cap, half
         * splat budget) regardless of their hardware. A visitor can still switch it off in the
         * help panel for their session; `?perf=0` also overrides. Default: false.
         */
        forceMobilePerformanceMode?: boolean;
        /**
         * Start desktop and laptop visitors in performance mode regardless of their stored
         * preference. Visitors can turn it off for their session; `?perf=0` also overrides.
         * Default: false. Independent of forceMobilePerformanceMode.
         */
        forceDesktopPerformanceMode?: boolean;
        /**
         * Render only when something on screen can have changed: input, camera movement, a
         * playing tour, LOD streaming, transitions, or a 1 Hz heartbeat. Scenes with features that
         * animate on their own (weather, particles, video/GIF hotspots, custom scripts, …) keep
         * rendering every frame automatically. Default: true. `?render=always` overrides.
         */
        onDemandRendering?: boolean;
        /** Whether measurement mode is enabled for this scene (shows ruler icon in viewer) */
        measurementsEnabled?: boolean;
        /** Scene scale calibration: real-world units per 1 scene unit (default: 1) */
        sceneScale?: number;
        /** Display unit for measurements (default: 'meters') */
        sceneScaleUnit?: 'meters' | 'centimeters' | 'feet' | 'inches';
        /** Custom color for measurement overlay lines/points (default: '#FF9800') */
        measurementColor?: string;
        /** When true, editor-authored default measurements ship with the scene to the viewer. */
        includeDefaultMeasurementsInViewer?: boolean;
        /** When true, the viewer auto-enters measurement mode on load so the tool is already toggled on. */
        defaultMeasurementsVisibleOnLoad?: boolean;
        /** When true, default measurement lines/labels remain visible even when measurement mode is closed. User-created measurements still clear on exit. */
        alwaysShowDefaultMeasurements?: boolean;
    };
    // Camera settings
    defaultCameraMode?: 'first-person' | 'orbit' | 'drone';
    allowedCameraModes?: string[];
    cameraMovementSpeed?: number;
    cameraRotationSensitivity?: number;
    /** Camera smoothness/damping (0.5-0.95). Lower = more responsive, higher = smoother. Default: 0.75 */
    cameraDamping?: number;
    invertCameraRotation?: boolean;
    cameraPosition?: number[];
    cameraRotation?: number[];
    fov?: number;
    /** Camera near clip plane (default 0.1). Mirror of editor's SaveFile.minClipPlane. */
    minClipPlane?: number;
    /** Camera far clip plane (default 1000). Mirror of editor's SaveFile.maxClipPlane. */
    maxClipPlane?: number;
    /** Scene background color (hex). Rendered behind the splat. */
    backgroundColor?: string;
    // Navigation
    includeScrollControls?: boolean;
    scrollButtonMode?: 'continuous' | 'incremental' | 'percentage' | 'waypoint';
    scrollAmount?: number;
    scrollSpeed?: number;
    transitionSpeed?: number;
    /** Easing curve for manual waypoint jumps. Autoplay and scroll remain continuous. */
    waypointEasing?: WaypointEasingMode;
    /** Tour path shape: 'smooth' spline (default) or 'linear' straight point-to-point segments. */
    pathInterpolation?: 'smooth' | 'linear';
    autoPlayEnabled?: boolean;
    /**
     * With autoplay on: the loader still closes at its usual point, but the tour does not start
     * moving until levels of detail have finished streaming for the opening view (pending LOD
     * downloads at 0 for a moment, at this device's budget). Meanwhile an "Adding in more detail"
     * status is shown, and after 5 s a "Start the tour anyways" button. Published viewer only;
     * scenes without levels of detail start as before. Absent/false = start as soon as the loader
     * closes.
     */
    autoPlayWaitForFullDetail?: boolean;
    /** Speed of autoplay in progress per second (default: 0.1 = 10 seconds per full tour) */
    autoplaySpeed?: number;
    /** Legacy alias for autoplaySpeed (SaveFile uses camelCase autoPlaySpeed) */
    autoPlaySpeed?: number;
    /** Loop behavior for playback: 'loop' restarts at beginning, 'pingpong' reverses direction, 'none' stops at end */
    loopMode?: 'loop' | 'pingpong' | 'none';
    // XR. Every scene offers VR (WebXR headsets); AR (beta, 8th Wall) is offered when
    // includeXR is on and xrMode isn't 'vr'.
    includeXR?: boolean;
    xrMode?: 'ar' | 'vr' | 'both';
    /** Base URL for self-hosted 8th Wall engine files. Defaults to /cdn/8thwall on same domain. */
    eighthWallBaseUrl?: string;
    // Custom script
    customScript?: string;
    // Reveal effect
    /** Reveal effect preset for splat loading animation. Use 'none' to disable. */
    revealEffect?: 'fast' | 'medium' | 'slow' | 'none';
    /** Reveal effect style: 'bloom' (fast burst with overshoot) or 'radial' (classic dot wave). Default: 'bloom' */
    revealStyle?: 'bloom' | 'radial';
    /** Legacy toggle from BabylonJS editor (maps to revealEffect: useNodeMaterial=false → 'none') */
    useNodeMaterial?: boolean;
    /** Swap transition type: 'scanline' (wipe) or 'dissolve' (noise cross-dissolve). Default: 'dissolve' */
    swapTransitionType?: 'scanline' | 'dissolve';
    /** Whether splat swap transitions (dissolve/wipe) are enabled. Default: true */
    enableSwapTransition?: boolean;
    /** Splat swap mode: 'chain' (sequential swap), 'compare' (interactive before/after slider), or 'menu' (no auto-trigger; swapped only from scene-menu items). Default: 'chain' */
    splatSwapMode?: 'chain' | 'compare' | 'menu';
    /** Compare mode settings (only used when splatSwapMode === 'compare') */
    compareSettings?: CompareConfig;
    // Splat Swap System
    /** Additional splats to swap to during navigation */
    additionalSplats?: SplatSwapPoint[];
    /** Keep preloaded splats in memory (true = hide/show, false = dispose/reload) */
    keepMeshesInMemory?: boolean;
    /** Explore sub-modes available inside Explore mode. Missing means both orbit and fly are available. 'walk' requires collision data. */
    enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'>;
    /** Default explore sub-mode for the initial splat. 'orbit' for aerial views, 'fly' for interior POV, 'walk' for first-person. Defaults to 'fly'. */
    initialSplatExploreMode?: 'orbit' | 'fly' | 'walk';
    /** Speed of auto-forward movement triggered by double-tap in explore mode (default: 1.0) */
    doubleTapMoveSpeed?: number;
    /** Whether head bob is enabled in walk mode (default: true) */
    headBobEnabled?: boolean;
    // Audio emitters
    /** Standalone spatial audio sources in the scene */
    audioEmitters?: AudioEmitterConfig[];
    // 4DGS Frame Sequence (mutually exclusive with splatUrl)
    /** Frame sequence configuration for 4DGS playback */
    frameSequence?: {
        frameUrls: string[]; // Array of PLY/SOG URLs
        fps?: number; // Playback FPS (default: 24)
        loop?: boolean; // Loop playback (default: true)
        preloadCount?: number; // Frames to preload ahead (default: 5)
        autoplay?: boolean; // Start playing immediately
        rotation?: [number, number, number]; // Entity euler angles [pitch, yaw, roll] in degrees
    };
    /** Splat relighting configuration (lights affect gaussian splats) */
    splatRelighting?: SplatRelightingConfig;
    /** LOD streaming display settings */
    lodSettings?: LodSettingsConfig;
    /** Mobile-specific LOD/renderer settings. Falls back to lodSettings when absent. */
    mobileLodSettings?: LodSettingsConfig;
    /** Initial orbit camera settings (position + pivot) */
    orbitCameraSettings?: OrbitCameraSettingsConfig;
    /** Initial walk mode camera settings (position + euler rotation in degrees, PlayCanvas coords) */
    walkCameraSettings?: { cameraPosition: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number } };
    /** When true, entering walk mode teleports the camera to walkCameraSettings. When false (default), walk mode uses the current camera position. */
    walkSpawnOverride?: boolean;
    /** Whether camera refocus triggers on single tap or double tap (default: 'single') */
    refocusTapMode?: 'single' | 'double';
    /** Entity keyframe animations */
    entityAnimations?: EntityAnimationConfig[];
    /** Post-processing settings */
    postProcessing?: PostProcessingConfig;
    /** Mirror reflection planes */
    mirrorPlanes?: MirrorPlaneData[];
    /** Feature flag for water surface effects. Water planes are ignored unless true. */
    enableWaterEffects?: boolean;
    /** Water surface planes for replacing broken pool / pond regions in splat scans */
    waterPlanes?: WaterPlaneData[];
    /** Procedural animated gaussian-splat cloud layers (effects/clouds). */
    cloudLayers?: CloudLayerData[];
    /** The scan's own baked sky: keep it, or replace it so clouds show at any height. */
    scanSky?: ScanSkySettings;
    /** Procedural weather effect (PlayCanvas 2.18+ splat-based) */
    weather?: WeatherConfig;
    /** The scene's wind: wind-reactive splat objects and areas, clouds that follow it (effects/wind). */
    wind?: SceneWindSettings;
    /** Audio-reactive splat shader effect (music-driven motion + color) */
    audioReactive?: AudioReactiveConfig;
    /** Segment data binary URL (.segments.bin - Uint16 per Gaussian) */
    segmentDataUrl?: string;
    /** Segment metadata JSON URL (.segments.json) */
    segmentMetaUrl?: string;
    /** Enable segment hover highlighting in viewer */
    enableSegmentHover?: boolean;
    /** Guided narration track synced to tour progress */
    narrationTrack?: NarrationTrackConfig;
    /** Background music bed that persists across portal teleports (set at the entry scene). */
    crossSceneAudio?: CrossSceneAudioConfig;
    /** Editor-authored measurements shipped with the scene. Rendered as locked defaults in the viewer — users can add their own but cannot delete these. */
    measurements?: MeasurementData[];
    /** Editor-authored area measurements (outlines clicked on a surface), shipped like `measurements`. */
    areaMeasurements?: AreaMeasurementData[];
    /**
     * Measurement sets: named groups of the saved measurements (e.g. "Main facade", "Roof"). A
     * measurement's `setId` names its set; a missing or unknown one counts as "Other measurements".
     * Every set shows when the scene opens (unless `measurementSetsStartHidden`); scripts switch
     * them (setMeasurementSetVisible), and so do visitors when `measurementSetsInLayersMenu` is on
     * (utils/measurementSets.ts).
     */
    measurementSets?: MeasurementSetData[];
    /**
     * Visitors pick measurement sets: the viewer's Measure panel (the ruler button) lists a tick box
     * per set that holds saved measurements (all ticked at first). Ticked sets' saved measurements
     * stay drawn whether or not the visitor is measuring. Default false. The name is historical:
     * viewers 2.12.1 and 2.12.2 put the tick boxes in a "Measurements" section of the Layers menu.
     */
    measurementSetsInLayersMenu?: boolean;
    /**
     * With `measurementSetsInLayersMenu`: every set starts unticked, so no saved measurement shows
     * until a visitor ticks a set in the Measure panel. Ignored without it. Default false (all ticked).
     */
    measurementSetsStartHidden?: boolean;
    /**
     * Annotation layers: coloured, see-through overlays painted onto the splat inside outlines
     * drawn on its surface, grouped into layers (e.g. building periods) that visitors switch on
     * and off from the viewer's layers panel. See effects/annotation-layers.ts.
     * Superseded by `regions`: read only when `regions` is absent (converted with regionsFromLegacy).
     */
    annotationLayers?: AnnotationLayersConfig;
    /**
     * Regions: colour, spotlight or label parts of the scan, each switched on from the start, by
     * triggers or by visitors in the Layers menu (effects/regions.ts). Replaces
     * `postProcessing.focusRegions` and `annotationLayers`; when present, a viewer that knows
     * Regions reads only this (the editor still writes the old fields for older viewers).
     */
    regions?: RegionsConfig;
    /**
     * Hotspot folders (dynamic-viewer/hotspotFolders.ts): one tree, any depth (`parentId`). A
     * hotspot or an extra waypoint names its folder with `folderId`; the `hotspots` array is in
     * the folders' order. Folders on their own change nothing visitors see; the visitor directory
     * lists hotspots and views by them.
     */
    hotspotFolders?: HotspotFoldersConfig;
    /** The visitor directory: a toolbar list of the hotspots by folder and the extra waypoints ("Views"). Absent: off. */
    visitorDirectory?: VisitorDirectoryConfig;
    /** AI re-skin images tied to camera poses. Editor generates them; viewer renders a palette-icon dropdown with overlay blend. */
    skins?: SceneSkinExport[];
}
/**
 * A single editor-authored measurement segment.
 * Rendered as a locked default in the viewer.
 */
export interface MeasurementData {
    id?: string;
    pointA: { x: number; y: number; z: number };
    pointB: { x: number; y: number; z: number };
    /** Optional user label */
    label?: string;
    /** The measurement set it belongs to (MeasurementSetData.id); missing or unknown = no set. */
    setId?: string;
}
/**
 * An editor-authored area measurement: an outline clicked onto a surface (a wall, a façade, a
 * roof slope), in the same scene coordinates as MeasurementData. The area is computed from the
 * points (utils/surfacePolygon.ts) with the scene's calibration scale, so it is not stored.
 * Rendered as a locked default in the viewer, gated by the same flags as `measurements`.
 */
export interface AreaMeasurementData {
    id?: string;
    points: Array<{ x: number; y: number; z: number }>;
    /** Optional user label */
    label?: string;
    /** The measurement set it belongs to (MeasurementSetData.id); missing or unknown = no set. */
    setId?: string;
}
/**
 * A measurement set: a named group of the scene's saved measurements (e.g. "Main facade",
 * "Left wall", "Roof") that is shown or hidden together (the API; visitors in the Measure panel
 * when the scene allows it). Sets are listed by `order` (then by their place in the array). More fields
 * may be added later (a colour, when it shows, a view to go to); viewers keep and ignore fields
 * they don't know.
 */
export interface MeasurementSetData {
    id: string;
    /** What the Measure panel calls it. */
    name: string;
    /** Position in the list (lowest first). */
    order?: number;
}
/** One measurement set as getMeasurementSets() reports it. */
export interface MeasurementSetInfo {
    /** The set's id, or 'other' for the saved measurements that belong to no set. */
    id: string;
    name: string;
    /** Its measurements are shown (visitors' own measurements always show). */
    visible: boolean;
    /** How many saved measurements (distances and areas) it holds. */
    count: number;
}
/**
 * Compare mode settings for before/after splat comparison slider
 */
export interface CompareConfig {
    /** Label for the "before" (primary) splat. Default: "Before" */
    beforeLabel?: string;
    /** Label for the "after" splat. Default: "After" */
    afterLabel?: string;
    /** Whether to show labels. Default: true */
    showLabels?: boolean;
    /** Initial slider position (0-100). Default: 50 */
    initialPosition?: number;
    /** Divider orientation. Default: 'vertical' */
    orientation?: 'vertical' | 'horizontal';
    /** Divider line color (hex). Default: '#ffffff' */
    lineColor?: string;
    /** Divider line opacity (0-1). Default: 0.6 */
    lineOpacity?: number;
    /** Allow camera movement while comparing. Default: true */
    allowCameraMovement?: boolean;
    /** Enable keyboard arrow key control. Default: true */
    enableKeyboard?: boolean;
    /** Enable double-click snap to 0/50/100%. Default: true */
    enableSnap?: boolean;
    /** Handle color (hex) — border, icon, and fill for minimal. Default: '#ffffff' */
    handleColor?: string;
    /** Handle opacity (0-1). Default: 0.6 */
    handleOpacity?: number;
    /** Handle visual style. Default: 'arrows' */
    handleStyle?: 'arrows' | 'circle' | 'minimal';
}

/**
 * Splat swap point - defines when to swap to a different splat during navigation
 */
export interface SplatSwapPoint {
    url: string;
    name?: string;
    /** Waypoint index to trigger the swap (-1 if using percentage) */
    waypointIndex: number;
    /** Percentage through tour to trigger the swap (-1 if using waypointIndex) */
    percentage: number;
    /** Optional skybox URL to apply when this splat is active */
    skyboxUrl?: string;
    /** Optional skybox rotation in radians */
    skyboxRotation?: number;
    /** Default camera mode when entering this splat in explore mode. 'orbit' for aerial views, 'fly' for interior POV. */
    defaultExploreMode?: 'orbit' | 'fly';
    /** Per-splat position override [x, y, z]. If undefined, uses the main splat's position. */
    position?: [number, number, number];
    /** Per-splat rotation override [x, y, z] in radians. If undefined, uses the main splat's rotation. */
    rotation?: [number, number, number];
    /** Per-splat scale override {x, y, z}. If undefined, uses the main splat's scale. */
    scale?: { x: number; y: number; z: number };
    /**
     * Swap by proximity (chain and menu modes): while the camera is inside this box or sphere
     * this splat is shown, in any camera mode. Editor scene coordinates, like overlay areas;
     * set waypointIndex and percentage to -1. Leaving returns to the splat shown before (in
     * chain mode, whatever the tour position calls for) unless `hideOnExit` is false, which
     * keeps the splat on screen until the tour (or another area) calls for a change. When
     * areas overlap, the one entered last wins. Captured video (beginFrameCapture, the
     * editor's Render Video) swaps on the first frame inside the area. Not run live in the
     * editor, where swaps are previewed by hand.
     */
    proximityArea?: RegionTrigger;
    // ---------------------------------------------------------------------------
    // Optional optimized/mobile variants (mirror the main splat). All optional and
    // purely additive — a swap with only `url` set behaves exactly as before.
    // Device selection picks the desktop set or the mobile set as a whole (never
    // mixed) and resolves a single URL with priority lodMeta > sog > compressedPly > url.
    // ---------------------------------------------------------------------------
    /** SOG-optimized desktop URL. */
    sogModelUrl?: string;
    /** Firebase Storage path for the SOG asset (for cleanup on remove). */
    sogStoragePath?: string;
    /** Compressed PLY desktop URL (PlayCanvas-preferred). */
    compressedPlyUrl?: string;
    /** Firebase Storage path for the compressed-PLY asset (for cleanup on remove). */
    compressedPlyStoragePath?: string;
    /**
     * LOD streaming manifest URL (lod-meta.json), e.g. from an LCC2 export. Loaded ahead of
     * the single-file variants in every mode: the splat renders unified with its own LOD
     * stream and the manifest's coordinate correction. Chain / menu swaps between two
     * unified splats (the primary counts as one) cut without the dissolve transition, which
     * needs each splat's own material.
     */
    lodMetaUrl?: string;
    /** Original mobile-variant URL (unoptimized). */
    mobileUrl?: string;
    /** Firebase Storage path for the original mobile asset (for cleanup on remove). */
    mobileStoragePath?: string;
    /** SOG-optimized mobile URL. */
    mobileSogModelUrl?: string;
    /** Firebase Storage path for the mobile SOG asset (for cleanup on remove). */
    mobileSogStoragePath?: string;
    /** Compressed PLY mobile URL. */
    mobileCompressedPlyUrl?: string;
    /** Mobile LOD streaming manifest URL — see `lodMetaUrl`. */
    mobileLodMetaUrl?: string;
    /**
     * Splat collision while this splat shows: the primary splat's (default, and what every
     * scene did before this field existed), its own (`collisionMeshUrl` / `voxelCollisionUrl`,
     * bound to this splat's transform), or none. Custom collision primitives always stay.
     */
    collision?: 'primary' | 'own' | 'none';
    /** Triangle-mesh collision (.collision.glb) for `collision: 'own'`, e.g. fused from an LCC2 export. */
    collisionMeshUrl?: string;
    /** Voxel octree collision (.voxel.json) for `collision: 'own'`. */
    voxelCollisionUrl?: string;
    /** Which of the two to query; defaults to 'mesh' when a mesh is set, else 'voxel'. */
    collisionSystem?: 'voxel' | 'mesh';
}
/**
 * "Swap splat on click": the splat a hotspot's or 3D model's activation shows, as a scene-menu
 * "Splat swap" item does (`goToSplat`). The swap runs alongside the element's other click
 * behaviour, so a Camera on Click move (an extra waypoint inside the building) carries the
 * visitor into the new splat. A target that is already on screen does nothing; a target that is
 * not one of the scene's splat swaps (deleted) is skipped. Not run in compare mode, or in the
 * editor viewport (which previews it on demand).
 */
export interface SplatSwapAction {
    /** One of the scene's `additionalSplats[].url`, or ORIGINAL_SPLAT_URL for the main splat. */
    targetSplatUrl: string;
}
/**
 * Waypoint interaction data structure
 */
export interface WaypointInteractionData {
    type: string;
    data?: Record<string, unknown>;
    [key: string]: unknown;
}
export interface WaypointCurveHandle {
    x: number;
    y: number;
    z: number;
}
export interface WaypointCurveOut {
    mode?: 'catmullRom' | 'bezier';
    /** Outgoing handle, relative to this waypoint's position. */
    handleOut?: WaypointCurveHandle;
    /** Incoming handle for the next waypoint, relative to the next waypoint's position. */
    handleInNext?: WaypointCurveHandle;
}
/**
 * Waypoint data structure
 */
export interface WaypointData {
    /** Optional formatting for info; info remains the plain-text fallback. */
    infoHtml?: string;
    id?: string;
    name?: string;
    title?: string; // Alternative name field
    info?: string;
    description?: string; // Alternative info field
    position?: {
        x: number;
        y: number;
        z: number;
    };
    x?: number;
    y?: number;
    z?: number;
    rotation?: {
        x: number;
        y: number;
        z: number;
    };
    fov?: number;
    duration?: number;
    interactions?: WaypointInteractionData[];
    /** Optional segment curve override for the segment starting at this waypoint. */
    curveOut?: WaypointCurveOut;
    /** Camera mode for this waypoint: 'first-person' locks look direction, 'orbit' allows free look around a target */
    cameraMode?: 'first-person' | 'orbit';
    /** Orbit target position (only used when cameraMode is 'orbit') */
    orbitTarget?: {
        x: number;
        y: number;
        z: number;
    };
    /** Distance from waypoint to auto-trigger interactions (default: 1.0 meters) */
    triggerDistance?: number;
    /**
     * Leave this waypoint out of the viewer's waypoints menu. The tour still visits it: Prev / Next
     * (unless `skipOnNextPrev`), scrolling, autoplay, progress and every waypoint-indexed setting are
     * unchanged. Default: listed.
     */
    hideInMenu?: boolean;
    /**
     * Prev / Next (buttons, `nextWaypoint()` / `prevWaypoint()`) step over this waypoint to the next
     * one that stops. The camera still passes through it on the way: scrolling, autoplay, progress,
     * the waypoints menu and every waypoint-indexed setting are unchanged. Default: Prev / Next stop
     * here. Ignored when every waypoint has it.
     */
    skipOnNextPrev?: boolean;
    /**
     * Keep this waypoint's name out of the banner at the top of the viewer when the tour arrives
     * here. The name still lists it in the waypoints menu; its info text still shows. Default: shown.
     */
    hideNameInViewer?: boolean;
    /**
     * List this waypoint under Views in the visitor directory (picking it goes there on the tour).
     * `id` places it in `visitorDirectory.viewOrder`. Default: not listed.
     */
    showInDirectory?: boolean;
    /**
     * Cutaway change when the tour arrives at this stop (scrolling, Next / Previous, the waypoints
     * menu, autoplay, a hotspot teleport, the stop the tour opens on). See CutawayAction.
     */
    cutaway?: CutawayAction;
    /**
     * Editor data: the extra waypoint (id and arrival settings) this stop was before it was added to
     * the tour, kept so the editor can restore it if it is moved back out. Ignored by the viewer.
     */
    extraView?: Record<string, unknown>;
}
/**
 * An extra waypoint: a named camera view that is NOT part of the tour. It is never on the tour
 * path (in the waypoints menu only with `showInMenu`) and never counted in progress, so it shifts no
 * waypoint index.
 * Hotspots fly to it (`HotspotData.teleportExtraWaypoint`) and scripts call
 * `viewer.goToExtraWaypoint(id)`; the camera travels there in explore mode.
 * Coordinates and rotation use the same conventions as tour waypoints (scene coordinates; the
 * editor's quaternion, or Euler degrees; FOV in degrees, or radians when below 3.5).
 * Keep in lockstep with ExtraWaypoint in src/types/SceneTypes.ts.
 */
export interface ExtraWaypointData {
    /** Stable id that hotspots and scripts refer to. */
    id: string;
    name?: string;
    /** Leave it out of the visitor directory's Views. Default: listed. */
    hideInDirectory?: boolean;
    /** The folder (hotspotFolders) the visitor directory lists it in. Absent: under Views. */
    folderId?: string;
    /** Also list it in the waypoints menu, after the tour waypoints. Default: not listed. */
    showInMenu?: boolean;
    /** Cutaway change on arrival (goToExtraWaypoint, the menus, the directory, a hotspot). See CutawayAction. */
    cutaway?: CutawayAction;
    x?: number;
    y?: number;
    z?: number;
    /** Alternative to x / y / z. */
    position?: {
        x: number;
        y: number;
        z: number;
    };
    rotation?: {
        x: number;
        y: number;
        z: number;
        w?: number;
    };
    fov?: number;
    /**
     * After arriving: 'orbit' orbits `orbitTarget` (the default when it is set); 'first-person' looks
     * around from the waypoint in fly mode. Without an orbit target the view is first-person.
     */
    cameraMode?: 'first-person' | 'orbit';
    /** The point the camera orbits after arriving (scene coordinates). */
    orbitTarget?: {
        x: number;
        y: number;
        z: number;
    };
    /**
     * Editor data: the tour-only settings this view had as a tour stop, kept so the editor can
     * restore them when it is added back to the tour. Ignored by the viewer.
     */
    tourStop?: Record<string, unknown>;
}
/**
 * Hotspot data structure
 */
/**
 * A single content block inside a hotspot's multi-media popup (`contentBlocks[]`).
 * Discriminated union keyed on `kind`; `id` is a stable key for reorder/delete.
 * The `image` kind holds MULTIPLE urls (a gallery). NO 'audio' kind (out of scope).
 * Keep this in lockstep with the editor copy in src/types/SceneTypes.ts.
 */
export type HotspotContentBlock = { id: string } & (
    | { kind: 'text'; text: string; textHtml?: string }
    | { kind: 'image'; urls: string[]; names?: string[]; layout?: 'grid' | 'carousel' | 'stacked' }
    | { kind: 'video'; url: string; name?: string; autoplay?: boolean; loop?: boolean; muted?: boolean }
    | { kind: 'link'; url: string; text?: string; buttonColor?: string }
    | { kind: 'pdf'; url: string; name?: string }
    | { kind: 'iframe'; url: string }
    | { kind: 'model'; url: string; name?: string }
);

/**
 * Linked actions: activating one hotspot or 3D model (the source) also drives a DIFFERENT
 * hotspot or model (the target), e.g. a PNG label hotspot that starts a video hotspot.
 * They run in addition to the source's own behaviour (popup, teleport, its own media) and
 * never cascade: an action never runs the target's own linked actions.
 *
 * Hotspot actions:
 *  - 'playVideo' | 'pauseVideo' | 'toggleVideo' | 'restartVideo': the target's video
 *    (a video hotspot, or any marker whose runtime entity carries a video element).
 *  - 'openPopup': open the target's popup as if it had been clicked.
 *  - 'playAudio' | 'stopAudio' | 'toggleAudio': the target's attached audio.
 * Model actions:
 *  - 'playAnimation' | 'stopAnimation' | 'toggleAnimation': the GLB's animations, played
 *    exactly as the model's own animation trigger plays them. 'stopAnimation' pauses in place.
 * Keyframe animation actions (target kind 'animation', id = EntityAnimationConfig.id; time-based
 * animations only):
 *  - 'playEntityAnimation': resume a paused animation (a finished 'once' one starts over).
 *  - 'pauseEntityAnimation': pause where it is.
 *  - 'toggleEntityAnimation': pause when playing, otherwise play.
 *  - 'restartEntityAnimation': start over from the beginning (honoring its delay).
 *
 * Shared by the editor (re-exported from src/types/SceneTypes.ts) and the runtime
 * (storysplat-viewer/src/dynamic-viewer/linkedActions.ts).
 */
export type LinkedHotspotActionType =
    | 'playVideo'
    | 'pauseVideo'
    | 'toggleVideo'
    | 'restartVideo'
    | 'openPopup'
    | 'playAudio'
    | 'stopAudio'
    | 'toggleAudio';
export type LinkedModelActionType = 'playAnimation' | 'stopAnimation' | 'toggleAnimation';
export type LinkedEntityAnimationActionType = 'playEntityAnimation' | 'pauseEntityAnimation' | 'toggleEntityAnimation' | 'restartEntityAnimation';
export type LinkedActionType = LinkedHotspotActionType | LinkedModelActionType | LinkedEntityAnimationActionType;
export type LinkedActionTargetKind = 'hotspot' | 'model' | 'animation';
export interface LinkedActionTarget {
    /** 'hotspot' = HotspotData.id, 'model' = CustomMeshConfig.id, 'animation' = EntityAnimationConfig.id */
    kind: LinkedActionTargetKind;
    id: string;
}
export interface LinkedAction {
    /** Stable key for the editor list; regenerated when the source is duplicated. */
    id: string;
    target: LinkedActionTarget;
    action: LinkedActionType;
}
/** Media of one stacked-marker layer. */
export type MarkerLayerKind = 'image' | 'video' | 'gif';
/**
 * One layer of a stacked marker (hotspot or portal `type: 'stack'`). Layers stack top to
 * bottom in array order, centred on the marker's vertical axis (plus `offsetX`), each at its
 * media's aspect ratio. The stack shares the marker's position, rotation, scale, billboard,
 * opacity, visibility range and click action. At most one video layer per stack is used
 * (the first); it is the marker's video (`entity.videoElement`).
 * Keep in lockstep with the editor copy in src/types/SceneTypes.ts.
 */
export interface MarkerLayer {
    /** Stable key for reorder/delete. */
    id: string;
    kind: MarkerLayerKind;
    /** Media URL: PNG/JPG/WebP for 'image', WebM/MP4 for 'video', GIF for 'gif'. */
    url: string;
    /** Original filename of an uploaded file (editor display; for a video layer it also tells the viewer a blob: URL is a WebM). */
    name?: string;
    /** Layer width in marker units (1 = an image marker at scale 1); height follows the media. Default 1. */
    width?: number;
    /** Horizontal offset in marker units (positive = right). Default 0. */
    offsetX?: number;
    /** Video layers: fallback URL when the main video fails to load (same as HotspotData.videoBackupUrl). */
    videoBackupUrl?: string;
    videoBackupName?: string;
    /** Video layers: transparency from a WebM's alpha channel; same rules as HotspotData.webmHasAlpha (`name` identifies a blob: URL). */
    webmHasAlpha?: boolean;
    /** Video layers: iOS dual-video alpha (main MP4 + grayscale mask), as on video hotspots. */
    useIOSVideoAlphaMethod?: boolean;
    forceIOSVideoAlphaMethodForAllDevices?: boolean;
    iosMainVideoUrl?: string;
    iosMainVideoName?: string;
    alphaMaskVideoUrl?: string;
    alphaMaskVideoName?: string;
}

/** A creator-defined orbit view for a hotspot click (scene coordinates). */
export interface HotspotOrbitView {
    cameraPosition: { x: number; y: number; z: number };
    pivotPoint: { x: number; y: number; z: number };
}

export interface HotspotData {
    id: string;
    /** The hotspot folder it is in (hotspotFolders); absent: not in a folder. */
    folderId?: string;
    position: {
        x: number;
        y: number;
        z: number;
    };
    type: 'sphere' | 'image' | 'video' | 'gif' | 'text' | 'link' | 'audio' | 'stack';
    data: unknown;
    scale?: number | {
        x: number;
        y: number;
        z: number;
    };
    /** Rotation in radians (BabylonJS Euler YXZ order) */
    rotation?: {
        x: number;
        y: number;
        z: number;
    };
    alwaysVisible?: boolean;
    /** Teleport to specific waypoint index on click (-1 to ignore) */
    teleportWaypoint?: number;
    /** Legacy alias for teleportWaypoint */
    teleportToWaypoint?: number;
    /** Teleport to specific scroll percentage (0-100) on click (-1 to ignore) */
    teleportPercent?: number;
    /** Legacy alias for teleportPercent */
    teleportToPercent?: number;
    /** Teleport animation mode: 'animate' smoothly travels to position, 'instant' jumps immediately */
    teleportMode?: 'animate' | 'instant';
    /**
     * On click, move the camera to this orbit view and stay in explore mode: the camera travels to
     * `cameraPosition` and then orbits `pivotPoint`. Scene coordinates, like `position`. Needs explore
     * mode with orbit enabled. A teleport (teleportWaypoint / teleportPercent) or an existing
     * teleportExtraWaypoint takes precedence.
     */
    orbitView?: HotspotOrbitView;
    /**
     * On click, fly the camera to this extra waypoint (ExtraWaypointData.id) and stay in explore mode,
     * orbiting its orbit target after arriving when it has one. Honors teleportMode. Precedence:
     * teleportWaypoint, then teleportPercent, then this, then orbitView. An id with no matching extra
     * waypoint (deleted) is ignored. Needs explore mode (orbit or fly enabled).
     */
    teleportExtraWaypoint?: string;
    /** Video URL for popup content (embedded video player in hotspot popup) */
    popupVideoUrl?: string;
    /** Autoplay the popup video as soon as the popup opens (starts muted so browsers allow it) */
    popupVideoAutoplay?: boolean;
    /** Loop the popup video */
    popupVideoLoop?: boolean;
    /** Start the popup video muted. Defaults to true while autoplaying. */
    popupVideoMuted?: boolean;
    /** GIF URL for GIF hotspots */
    gifUrl?: string;
    /** Image URL for image hotspots */
    imageUrl?: string;
    /** Video URL for video hotspots */
    videoUrl?: string;
    /** Original file name of the uploaded video (identifies a WebM behind an opaque blob: URL). */
    videoName?: string;
    /** Backup video URL (fallback for codec compatibility) */
    videoBackupUrl?: string;
    /** Hotspot title */
    title?: string;
    /** Hotspot description/information */
    information?: string;
    informationHtml?: string;
    /** Photo URL for popup content */
    photoUrl?: string;
    /** External link URL */
    externalLinkUrl?: string;
    /** External link text */
    externalLinkText?: string;
    /** iFrame URL for popup content */
    iframeUrl?: string;
    /** Hotspot color (hex) */
    color?: string;
    /** Activation mode: 'click', 'hover', or 'none' (none = invisible/no interaction) */
    activationMode?: 'click' | 'hover' | 'none';
    /** Whether to use scene lighting on the hotspot */
    useLighting?: boolean;
    /** Opacity mode: 'static' or 'animated' */
    opacityMode?: 'static' | 'animated';
    /** Static opacity (0-1) */
    opacity?: number;
    /** Animated opacity settings */
    opacityAnimation?: {
        startPercent: number;
        endPercent: number;
        startOpacity: number;
        endOpacity: number;
    };
    /** Visibility range */
    visibilityRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    /** Billboard mode - always face camera */
    billboard?: boolean;
    /** Billboard range control */
    billboardRangeStart?: number;
    billboardRangeEnd?: number;
    /**
     * Keep the same size on screen: the hotspot is scaled with the camera's distance (and field of
     * view), so zooming in doesn't make it huge and zooming out doesn't make it tiny.
     */
    keepScreenSize?: boolean;
    /** With keepScreenSize: camera distance (scene units) at which it shows its set size. Default 5. */
    screenSizeDistance?: number;
    /** With keepScreenSize: never smaller than this multiple of its set size. Default 0.1. */
    screenSizeMin?: number;
    /** With keepScreenSize: never larger than this multiple of its set size. Default 10. */
    screenSizeMax?: number;
    /** Media trigger mode */
    mediaTriggerMode?: 'autoplay' | 'click' | 'proximity' | 'scroll';
    /** Proximity distance for proximity trigger */
    proximityDistance?: number;
    /** Whether to pause video when camera leaves proximity (default: true) */
    pauseOnLeaveProximity?: boolean;
    /** Video loop */
    videoLoop?: boolean;
    /** Video muted */
    videoMuted?: boolean;
    /**
     * Transparency from the video's own alpha channel (VP9 WebM). Unset: on for a video
     * recognised as WebM (Firebase / CDN / blob: URLs and `videoName` count, not only a URL
     * ending in `.webm`). false: off. true: also on when neither the URL nor `videoName` tells
     * the format. Never applies to a recognised non-WebM (MP4, MOV) video.
     */
    webmHasAlpha?: boolean;
    /** Use iOS video alpha method */
    useIOSVideoAlphaMethod?: boolean;
    /** Force iOS video alpha method for all devices */
    forceIOSVideoAlphaMethodForAllDevices?: boolean;
    /** iOS main video URL */
    iosMainVideoUrl?: string;
    /** Original file name of the uploaded iOS main video (identifies its format behind a blob: URL). */
    iosMainVideoName?: string;
    /** Alpha mask video URL */
    alphaMaskVideoUrl?: string;
    /** Audio URL for hotspot */
    audioUrl?: string;
    /** Audio loop */
    audioLoop?: boolean;
    /** Audio volume (0-1) */
    audioVolume?: number;
    /** Spatial audio */
    audioSpatial?: boolean;
    /** Audio distance model */
    audioDistanceModel?: 'linear' | 'inverse' | 'exponential';
    /** Audio max distance */
    audioMaxDistance?: number;
    /** Audio ref distance */
    audioRefDistance?: number;
    /** Audio rolloff factor */
    audioRolloffFactor?: number;
    /** How hotspot audio is triggered: proximity (default), autoplay, click, hover */
    audioTriggerMode?: 'proximity' | 'autoplay' | 'click' | 'hover';
    /** How click-triggered hotspot audio toggles on repeated clicks */
    audioClickBehavior?: 'pause-resume' | 'stop-restart';
    /** Stop audio when camera leaves proximity range. Default: true */
    audioStopOnExit?: boolean;
    /** Popup background color */
    backgroundColor?: string;
    /** Popup background opacity (0-1, default 0.75) */
    backgroundAlpha?: number;
    /** Popup text color */
    textColor?: string;
    /** Popup font size */
    fontSize?: number;
    /** Popup font family */
    fontFamily?: string;
    /** Popup close button color (hex) */
    closeButtonColor?: string;
    /**
     * Where the popup opens: one of the nine screen positions (like overlays). Unset keeps
     * the centered popup (or the global Theme Editor position).
     */
    popupAnchor?: LayoutAnchor;
    /**
     * 'drawer' opens the popup as a full-height edge-to-edge column on the popupAnchor side;
     * 'floating' keeps the popup even when the theme makes drawers the default. Unset follows
     * the theme (ViewerTheme.hotspotPopupLayout).
     */
    popupLayout?: 'floating' | 'drawer';
    /** Content type for the hotspot popup */
    contentType?: 'none' | 'text' | 'image' | 'video' | 'gif' | 'audio' | 'iframe' | 'link' | 'model' | 'pdf';
    /** External link button color (hex) */
    externalLinkButtonColor?: string;
    /** URL for a 3D model (GLB/GLTF) shown in a lightbox popup */
    modelUrl?: string;
    /** URL for an uploaded PDF document shown in popup */
    pdfUrl?: string;
    /** Optional leader line drawn from the hotspot to a world-space target point. */
    leaderLineEnabled?: boolean;
    /** World-space endpoint of the leader line (BabylonJS convention — viewer handles Z flip). */
    leaderLineTarget?: { x: number; y: number; z: number };
    /** Hex color for the line (default '#ffffff'). */
    leaderLineColor?: string;
    /** Line thickness in pixels (default 2). */
    leaderLineWidth?: number;
    /** Opacity 0–1 (default 1). Reserved for future use. */
    leaderLineOpacity?: number;
    /**
     * Optional ordered multi-media content blocks for the popup. When present and
     * non-empty, the renderer shows these instead of the single `contentType` path.
     * Absent/empty → the legacy single-content path runs unchanged. Defaults to undefined.
     */
    contentBlocks?: HotspotContentBlock[];
    /**
     * Actions this hotspot's activation also runs on OTHER hotspots or models (see
     * LinkedAction). They run on click (activationMode 'click' or 'none'), or when the
     * pointer enters a hover-activated hotspot, in addition to the hotspot's own behaviour.
     * Absent = none (every existing scene behaves as before).
     */
    linkedActions?: LinkedAction[];
    /**
     * Cutaway change this hotspot's activation also makes (see CutawayAction): with its linked
     * actions, on click (or pointer-enter for hover activation), from the directory and from
     * `triggerHotspot`. Absent = the cutaway stays as it is.
     */
    cutaway?: CutawayAction;
    /**
     * Splat this hotspot's activation shows (see SplatSwapAction): with its cutaway and linked
     * actions, on click (or pointer-enter for hover activation), from the visitor directory, in
     * VR and from `triggerHotspot`. Absent = the splat on screen stays.
     */
    splatSwap?: SplatSwapAction;
    /**
     * Layers of a `type: 'stack'` marker, top to bottom. The video layer (at most one) plays
     * by mediaTriggerMode / videoLoop / videoMuted / pauseOnLeaveProximity like a video hotspot.
     * Ignored by other types.
     */
    markerStack?: MarkerLayer[];
    /** Gap between stack layers in marker units; negative overlaps them. Default 0. */
    markerStackSpacing?: number;
}
/**
 * Portal data structure - links to another StorySplat scene
 * Only supported in the V2 (PlayCanvas) viewer
 */
export interface PortalData {
    /** Keep the marker's apparent size as the camera moves, like hotspots. */
    keepScreenSize?: boolean;
    screenSizeDistance?: number;
    screenSizeMin?: number;
    screenSizeMax?: number;
    id: string;
    position: {
        x: number;
        y: number;
        z: number;
    };
    // Portal target
    targetSceneId: string; // StorySplat scene ID to navigate to
    targetSceneName?: string; // Display name for UI
    targetSceneThumbnail?: string; // Thumbnail for preview
    // Visual appearance (same options as hotspots)
    type: 'sphere' | 'image' | 'video' | 'gif' | 'plane' | 'panorama' | 'stack';
    color?: string; // For sphere type
    imageUrl?: string; // For image type
    videoUrl?: string; // For video type
    /** Original file name of the uploaded video (identifies a WebM behind an opaque blob: URL). */
    videoName?: string;
    /** Video type: transparency from a WebM's alpha channel; same rules as HotspotData.webmHasAlpha. */
    webmHasAlpha?: boolean;
    gifUrl?: string; // For gif type
    panoramaUrl?: string; // For panorama type (equirectangular 360° image)
    scale?: number | {
        x: number;
        y: number;
        z: number;
    };
    rotation?: {
        x: number;
        y: number;
        z: number;
    };
    // Display options
    title?: string; // Label shown near portal
    /** Whether to show a confirmation prompt before navigating */
    confirmNavigation?: boolean;
    billboard?: boolean; // Always face camera
    opacity?: number; // 0-1
    useLighting?: boolean; // Apply scene lighting
    // Visibility control
    visibilityRange?: {
        type: 'percentage' | 'waypoint';
        start: number;
        end: number;
    };
    // Activation
    activationMode: 'click' | 'proximity' | 'collision';
    proximityDistance?: number; // Auto-trigger distance (for proximity mode)
    // Scene menu options
    menuOnly?: boolean; // If true, no 3D mesh — appears only in scene navigation menu
    isCurrentScene?: boolean; // Marker: this menu entry IS the current scene (rendered "selected", non-navigating)
    menuPath?: string; // Folder path for menu organization, e.g. "Building A/Floor 1"
    /**
     * Unified scene-menu sort key shared across portals and SceneMenuLinks.
     * When set on items, the menu sorts by it. Undefined on legacy scenes — the
     * viewer falls back to "portals first, then links, in array order" so existing
     * published menus don't reorder. Keep in lockstep with editor Portal.menuOrder.
     */
    menuOrder?: number;
    /**
     * Whether this 3D portal appears in the scene navigation menu. Default
     * (undefined) = shown. When false, the portal keeps its in-world 3D presence
     * but is omitted from the menu. Ignored for menu-only portals (always shown).
     */
    showInMenu?: boolean;
    // Leader line settings (mirrors HotspotData)
    leaderLineEnabled?: boolean;
    leaderLineTarget?: { x: number; y: number; z: number };
    leaderLineColor?: string;
    leaderLineWidth?: number;
    leaderLineOpacity?: number;
    /**
     * Layers of a `type: 'stack'` marker, top to bottom (mirrors HotspotData). A portal's
     * video layer autoplays muted and loops, like a video portal.
     */
    markerStack?: MarkerLayer[];
    /** Gap between stack layers in marker units; negative overlaps them. Default 0. */
    markerStackSpacing?: number;
}
/**
 * Customizable button labels for internationalization (i18n)
 */
export interface ButtonLabels {
    // Camera mode buttons
    tour?: string;
    explore?: string;
    hybrid?: string;
    walk?: string;
    orbit?: string;
    fly?: string;
    // Navigation buttons
    next?: string;
    previous?: string;
    // Control buttons
    startExperience?: string;
    fullscreen?: string;
    mute?: string;
    unmute?: string;
    share?: string;
    capturingPhoto?: string;
    photoReady?: string;
    downloadPhoto?: string;
    photoDownloaded?: string;
    copyLink?: string;
    linkCopied?: string;
    shareFailed?: string;
    // Waypoint list
    waypoints?: string;
    // Scene menu
    scenes?: string;
    // Popup/dialog strings
    close?: string;
    yes?: string;
    cancel?: string;
    switchScenes?: string;
    // Hotspot defaults
    hotspotDefaultTitle?: string;
    openExternalLink?: string;
    // XR buttons
    vr?: string;
    ar?: string;
    exitVr?: string;
    exitAr?: string;
    exitPanorama?: string;
    // Loading states
    loading?: string;
    loadingScene?: string;
    // Help panel
    helpTitle?: string;
    helpCameraModes?: string;
    helpTourDesc?: string;
    helpExploreDesc?: string;
    helpWalkDesc?: string;
    // Help panel - control instructions
    helpTourControls?: string;
    helpTourScroll?: string;
    helpTourDrag?: string;
    helpExploreControls?: string;
    helpExploreLMB?: string;
    helpExploreRMB?: string;
    helpExploreWASD?: string;
    helpExploreShift?: string;
    helpExploreScroll?: string;
    helpExploreDblClick?: string;
    helpWalkControls?: string;
    helpWalkClick?: string;
    helpWalkWASD?: string;
    helpWalkMouse?: string;
    helpWalkShift?: string;
    helpWalkSpace?: string;
    // Error messages
    errorWebGLTitle?: string;
    errorWebGLMessage?: string;
    // Percentage label
    percentageFormat?: string;
}
/**
 * Every customizable viewer string: ButtonLabels plus strings added after the legacy BabylonJS
 * HTML exporter was frozen. That exporter mirrors ButtonLabels as Required<ButtonLabels>, so new
 * keys are declared here instead; `uiOptions.buttonLabels` and `setButtonLabels()` accept them.
 */
export interface ViewerButtonLabels extends ButtonLabels {
    /** The VR button as a large prompt, on pages that asked for VR (?vr=1, VR mode, the vr option). */
    enterVr?: string;
    /** Centred status while autoplay waits for levels of detail (scene option autoPlayWaitForFullDetail). */
    addingMoreDetail?: string;
    /** Button that starts the tour without waiting for the remaining detail. */
    startTourAnyway?: string;
    // Controls card, shown once after the scene loads (tour and explore modes)
    /** Card heading when only the mouse is needed (tour-only scenes, orbit). */
    controlsTitleMouse?: string;
    /** Card heading when the keyboard also moves the camera (tour with explore, fly, walk). */
    controlsTitleMouseKeyboard?: string;
    /** Drag with the mouse to look around (tour, fly, walk). */
    controlsLookAround?: string;
    /** Scroll wheel moves along the tour. */
    controlsTourScroll?: string;
    /** Drag with the mouse to orbit (orbit mode). */
    controlsOrbitRotate?: string;
    /** Scroll wheel zooms (orbit mode). */
    controlsOrbitZoom?: string;
    /** Caption under the movement keys. */
    controlsMove?: string;
    /** Touch devices, tour mode. */
    controlsTouchTour?: string;
    /** Touch devices, orbit mode. */
    controlsTouchOrbit?: string;
    /** Touch devices, fly and walk modes. */
    controlsTouchMove?: string;
    // Measure panel (the ruler button's menu)
    /** The ruler button's accessible name. */
    measureButton?: string;
    /** The panel's heading. */
    measureTitle?: string;
    /** Removes every measurement the visitor made. */
    measureClearAll?: string;
    /** Accessible name of the Distance / Area switch. */
    measureType?: string;
    measureDistance?: string;
    measureArea?: string;
    /** Shown before the first measurement in Distance mode. */
    measureDistanceHint?: string;
    /** Shown before the first measurement in Area mode. */
    measureAreaHint?: string;
    /** While an outline has one point. */
    measureAreaOnePoint?: string;
    /** While an outline has two points; {n} is the count. */
    measureAreaPoints?: string;
    /** Once the outline can be closed. */
    measureAreaClose?: string;
    /** Button that closes the outline. */
    measureAreaFinish?: string;
    /** Tooltip of the lock on the creator's measurements. */
    measureLocked?: string;
    /** Tooltip of an area whose points are not on one flat surface. */
    measureAreaNotFlat?: string;
    /** Tooltip of an area whose outline crosses itself. */
    measureAreaCrossing?: string;
    /** Heading of the measurement sets visitors pick (measurementSetsInLayersMenu). */
    measureSets?: string;
    /** The row for the creator's measurements in no set. */
    measureOtherSet?: string;
    /** The row that ticks every set (or unticks them all). */
    measureAllSets?: string;
    // Visitor directory (visitorDirectory; its title is set by the creator)
    /** Heading of the views (extra and tour waypoints) not in a folder. */
    directoryViews?: string;
    /** Placeholder of the search box. */
    directorySearch?: string;
    /** Accessible name of the search box. */
    directorySearchLabel?: string;
    /** Search found nothing (the directory lists only hotspots). */
    directoryNoMatch?: string;
    /** Search found nothing (the directory lists hotspots and views). */
    directoryNoMatchWithViews?: string;
    /** Beside a hotspot its own settings hide right now. */
    directoryUnavailable?: string;
    /** A folder's tick box; {name} is the folder's name. */
    directoryShowFolder?: string;
    /** A folder's count when it holds one hotspot; {n} is the number. */
    directoryHotspotCount?: string;
    /** A folder's count when it holds several hotspots; {n} is the number. */
    directoryHotspotsCount?: string;
    /** A folder's count when it holds one item (hotspots and views together); {n} is the number. */
    directoryItemCount?: string;
    /** A folder's count when it holds several items (hotspots and views); {n} is the number. */
    directoryItemsCount?: string;
    // VR: the wrist menu and floating panels a headset shows instead of the page UI
    /** The wrist menu's heading. */
    vrMenu?: string;
    /** The Measurements panel's switch for the saved measurements. */
    vrShowMeasurements?: string;
    /** Saves a popup's link or web page to open after leaving VR. */
    vrSaveLink?: string;
    /** That button once the link is saved. */
    vrLinkSaved?: string;
    /** The wrist menu entry for the saved links. */
    vrSavedLinks?: string;
    /** Their panel, and the page's list after VR. */
    vrSavedLinksTitle?: string;
    vrPlay?: string;
    vrPause?: string;
    /** A popup's media tabs. */
    vrPhotos?: string;
    vrVideo?: string;
    vrPdf?: string;
    /** A popup with a 3D model (it can't show in VR). */
    vrModelOutsideVr?: string;
    /** In place of an HTML mesh (page content a headset can't show). */
    vrWebContent?: string;
}
/**
 * Viewer instance returned by createViewer
 */
export interface CutawayOptions {
    enabled?: boolean;
    /** Plane position: 0 = bottom of the splat, 1 = top. */
    level?: number;
    direction?: 'above' | 'below';
    /**
     * Seconds the plane takes to slide to `level` (or, with `enabled: false`, out of the way
     * before the cutaway turns off). Omitted or 0: at once.
     */
    duration?: number;
}
/**
 * A cutaway change a scene element makes when it is activated: a hotspot or 3D model click (on
 * hover-in for hover activation), a tour stop or extra waypoint on arrival, a scene-menu item
 * click. It runs alongside everything else the element does (camera move, popup, media, linked
 * actions), so the plane slides while the camera travels.
 *
 * - `enabled: true` cuts the scene at `level`; `enabled: false` slides the plane away and shows
 *   the whole scene again. An element without the field leaves the cutaway as it is.
 * - Works whether or not the visitor's Cutaway button is on (`uiOptions.cutawayEnabled`); with it
 *   on, the slider follows and visitors can move or clear the cut. Turning the cutaway on leaves
 *   walk mode and suspends collision like the button does (`cutawayDisablesCollision`).
 * - The latest trigger wins, except that a click's own cutaway outranks the tour stops and the
 *   extra waypoint its camera action goes to.
 * - The editor viewport doesn't run them (it has no Cutaway button to clear a cut); its Preview
 *   button does.
 *
 * Shared by the editor (re-exported from src/types/SceneTypes.ts) and the runtime
 * (storysplat-viewer/src/effects/cutaway.ts).
 */
export interface CutawayAction {
    /** True: cut the scene at `level`. False: turn the cutaway off. */
    enabled: boolean;
    /** Plane position, 0 = bottom of the splat, 1 = top (the slider's percentage / 100). Default 0.5. */
    level?: number;
    /** Which side of the plane disappears. Omitted: the side it already hides (the scene's Hidden side at first). */
    direction?: 'above' | 'below';
    /** Seconds the plane takes to slide there; 0 jumps. Default 1. */
    duration?: number;
}
export interface CutawayStatus {
    /** False when the scene has no cutaway: no Cutaway button and no element that runs a CutawayAction. */
    available: boolean;
    enabled: boolean;
    level: number;
    direction: 'above' | 'below';
    /** True while the cutaway has collision switched off (uiOptions.cutawayDisablesCollision). */
    collisionsSuspended: boolean;
}
/**
 * Stereo 3D output formats. 'sbs' / 'tb' are the half side-by-side and half top-and-bottom
 * frames 3D TVs and projectors expect; 'sbs-full' keeps each eye at full shape in its half;
 * 'anaglyph' is for red/cyan glasses.
 */
export type StereoFormat = 'sbs' | 'tb' | 'sbs-full' | 'anaglyph';
export interface StereoOptions {
    /** 3D strength, 0 to 2 (1 = the 1/30 rule). */
    depth?: number;
    /** Zero-parallax distance in scene units; 0 or null returns to automatic. */
    screenDistance?: number | null;
    /** Show each eye's image to the other eye (cross-eyed viewing, glasses worn flipped). */
    swapEyes?: boolean;
}
export interface CapturePhotoOptions {
    /**
     * Hide hotspot/portal markers, leader lines and the reticle for this capture only.
     * Custom 3D models and video hotspot planes stay in the image (as do all layers of a stacked
     * hotspot that has a video layer).
     */
    hideEntities?: boolean;
}

/** A camera pose in PlayCanvas world coordinates; `fov` is the camera's vertical field of view in degrees. */
export interface CameraPose {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
    fov: number;
}

/** Tour-path playback info for offline rendering (defaultDurationSec = one full pass at the scene's autoplay speed). */
export interface PathRenderInfo {
    waypointCount: number;
    loopMode: 'loop' | 'pingpong' | 'none';
    defaultDurationSec: number;
}

/**
 * Offline stand-in for the live autoplay camera (see ViewerInstance.createPathPoseFollower).
 * `timeConstantSec` is the damping's time constant τ: the camera trails a moving target by
 * about velocity × τ and closes the gap by a factor of e every τ.
 */
export interface CameraPoseFollower {
    step: (target: CameraPose, dtSec: number) => CameraPose;
    reset: () => void;
    readonly timeConstantSec: number;
}

export interface FrameCaptureOptions {
    /** Output width in pixels (even, 16–8192). */
    width: number;
    /** Output height in pixels (even, 16–8192). */
    height: number;
    /**
     * Hide hotspot/portal markers and leader lines in captured frames (default true); custom
     * 3D models and video hotspot planes stay. The reticle is always hidden.
     */
    hideMarkers?: boolean;
    /**
     * Frame rate of the video being captured (default 30). Each renderFrame call advances
     * splat swap transitions (dissolve / wipe) by 1 / fps, so a transition spans the same
     * stretch of video as it lasts live, whatever the capture's real speed.
     */
    fps?: number;
}

/** Per-frame input to FrameCaptureSession.renderFrame. */
export interface FrameCaptureFrameOptions {
    /**
     * Tour timeline progress [0, 1] of this frame — the value its pose came from via
     * getPathPoseAtProgress. When given, splat swaps triggered by waypoint or tour percentage
     * are applied for it, as the live tour would. Leave it out for frames that are not on the
     * tour (a custom camera path): those only get proximity-area swaps.
     */
    progress?: number;
}

/** A running fixed-resolution capture started with ViewerInstance.beginFrameCapture. */
export interface FrameCaptureSession {
    /** The viewer canvas; holds the latest captured frame right after renderFrame resolves. */
    readonly canvas: HTMLCanvasElement;
    readonly width: number;
    readonly height: number;
    /**
     * Render the scene from `pose` once splat sorting and streaming have caught up. Resolves
     * in the task that rendered: read `canvas` (e.g. `new VideoFrame(canvas)`) immediately
     * after awaiting, before any other await, while the drawing buffer is still valid.
     *
     * Splat swaps happen as the creator configured them: a proximity area swaps on the first
     * frame whose pose is inside it, a waypoint / percentage swap on the first frame whose
     * `frame.progress` reaches it. A frame that starts a swap waits until the new splat has
     * loaded, and transitions advance by the video's frame time (see FrameCaptureOptions.fps).
     * The first frame shows its swap state directly, without a transition. end() puts back
     * the splat and skybox that were on screen before the capture.
     */
    renderFrame: (pose: CameraPose, frame?: FrameCaptureFrameOptions) => Promise<void>;
    /** Restore resolution, camera, camera drivers and the splat on screen. Idempotent. */
    end: () => void;
}

export interface ViewerInstance {
    // PlayCanvas app reference
    app: pc.Application;
    canvas: HTMLCanvasElement;
    // Capture/share API
    /** Capture the current view as a JPEG. Rejects while a frame capture (beginFrameCapture) is running. */
    capturePhoto: (options?: CapturePhotoOptions) => Promise<Blob>;
    /** Capture the scene at a target aspect (e.g. 16/9) with the SAME vertical FOV — a real
     *  wider render (not padded/outpainted). Used by Virtual Staging. Returns a PNG blob.
     *  Rejects while a frame capture (beginFrameCapture) is running. */
    captureWide: (targetAspect: number) => Promise<Blob>;
    shareCurrentView: () => Promise<void>;
    /**
     * Toggle or move the cutaway plane (requires `uiOptions.cutawayEnabled`, or an element of the
     * scene with a `cutaway` action). `level` runs from 0 (bottom of the splat) to 1 (top); the
     * hidden side is `direction`; `duration` (seconds) slides the plane there.
     */
    setCutaway: (options: CutawayOptions) => void;
    getCutaway: () => CutawayStatus;
    /**
     * Enter or leave photo mode: interface chrome, hotspot and portal markers, leader lines and
     * the reticle are hidden (custom 3D models and video hotspot planes stay) while the visitor
     * keeps full camera control and frames the shot.
     */
    setPhotoMode: (enabled: boolean) => void;
    getPhotoMode: () => boolean;
    /**
     * Switch stereo 3D output on (a format) or off (null). Ignored in scenes with
     * `uiOptions.stereoEnabled === false`. The split formats draw the interface once per eye; call
     * it from a user gesture to also go fullscreen.
     * Ignored during a VR or AR session.
     */
    setStereoMode: (format: StereoFormat | null) => void;
    getStereoMode: () => StereoFormat | null;
    /** Whether setStereoMode can switch stereo on now: the scene allows it and no compare slider, VR or AR session is in the way. */
    isStereoAvailable: () => boolean;
    setStereoOptions: (options: StereoOptions) => void;
    /** Capture a clean image of the current view and download it. Resolves with the image. */
    takePhoto: () => Promise<Blob>;
    // Camera pose & offline capture API (video export, virtual staging)
    /** The live camera pose (PlayCanvas world coordinates, quaternion rotation, vertical FOV in degrees). */
    getCameraPose: () => CameraPose;
    /**
     * Freeze whichever camera driver is active (tour, walk, explore) so the camera holds still,
     * optionally moving it to `pose` first — e.g. to keep an overlay aligned with a captured view.
     * Calling it again while held just moves the held camera. Release with releaseCameraPose.
     * Throws while a frame capture (beginFrameCapture) is running.
     */
    holdCameraPose: (pose?: CameraPose) => void;
    /** Undo holdCameraPose: tour eases back onto the path; walk/explore return to the pre-hold pose. */
    releaseCameraPose: () => void;
    /**
     * Deterministic tour-path pose at normalized timeline progress [0,1] (PlayCanvas world
     * coordinates, ready for camera.setPosition/setRotation). Bypasses the damped
     * interpolation entirely — used for offline frame-by-frame video rendering.
     * Returns null when the scene has fewer than 2 waypoints.
     */
    getPathPoseAtProgress: (progress: number) => CameraPose | null;
    /** Tour-path playback info for offline rendering. */
    getPathRenderInfo: () => PathRenderInfo;
    /**
     * Offline stand-in for the live autoplay camera: feed it the path pose for each
     * frame (from getPathPoseAtProgress) with the frame time, and it returns the pose
     * of a camera damped toward it exactly as the live tour damps its camera, using the
     * scene's current Transition Speed. The first step seeds the camera on the target.
     */
    createPathPoseFollower: () => CameraPoseFollower;
    /**
     * Start a deterministic fixed-resolution capture for video export: pauses playback,
     * freezes camera drivers and input, hides the reticle (and markers unless
     * `hideMarkers: false`), and switches the backbuffer to width×height until `end()`.
     * Throws if a capture is already running.
     */
    beginFrameCapture: (options: FrameCaptureOptions) => FrameCaptureSession;
    // Navigation API
    goToWaypoint: (index: number) => void;
    nextWaypoint: () => void;
    prevWaypoint: () => void;
    getCurrentWaypointIndex: () => number;
    getWaypointCount: () => number;
    /**
     * Fly the camera to an extra waypoint (a named view off the tour path) by its id, in explore mode
     * (switching from tour or walk if needed), then orbit its orbit target or look around from there
     * in fly mode. Works in any camera mode. `instant: true` jumps instead of travelling. Returns false
     * when there is no such extra waypoint, the scene has no explore mode, or an XR session owns the
     * camera.
     */
    goToExtraWaypoint: (id: string, options?: { instant?: boolean }) => boolean;
    /** The scene's extra waypoints (named views off the tour path), for goToExtraWaypoint. */
    getExtraWaypoints: () => Array<{ id: string; name: string }>;
    // Camera API
    setPosition: (x: number, y: number, z: number) => void;
    setRotation: (x: number, y: number, z: number) => void;
    getPosition: () => {
        x: number;
        y: number;
        z: number;
    };
    getRotation: () => {
        x: number;
        y: number;
        z: number;
    };
    // Playback API
    play: () => void;
    pause: () => void;
    stop: () => void;
    isPlaying: () => boolean;
    // Entity Animation API (keyframe entity animations; no-ops when the scene has none)
    /** Play entity keyframe animations. Pass an animation id to play one, omit to play all enabled independent animations. Restarts a finished 'once' animation. */
    playEntityAnimation: (id?: string) => void;
    /** Pause entity keyframe animations. Pass an animation id to pause one, omit to pause all. */
    pauseEntityAnimation: (id?: string) => void;
    /** Reset entity keyframe animations to their start. Pass an animation id to reset one, omit to reset all. */
    resetEntityAnimation: (id?: string) => void;
    /** List the scene's entity keyframe animations (with their duration, playback mode, trigger and whether they are playing). */
    getEntityAnimations: () => Array<import('../dynamic-viewer/EntityAnimationSystem').EntityAnimationInfo>;
    /** Restart a time-based entity animation from its beginning (honoring its delay) and play it. */
    restartEntityAnimation: (id: string) => void;
    /** Jump a time-based entity animation to `seconds` (clamped to its duration), keeping it playing or paused. */
    seekEntityAnimation: (id: string, seconds: number) => void;
    /** True while a time-based entity animation is playing (or waiting out its delay). */
    isEntityAnimationPlaying: (id: string) => boolean;
    // 4DGS Frame Sequence API (only available when frameSequence is configured)
    isFrameSequencePlaying: () => boolean;
    setFrame?: (index: number) => void;
    getCurrentFrame?: () => number;
    getTotalFrames?: () => number;
    setFps?: (fps: number) => void;
    getFps?: () => number;
    getFrameProgress?: () => number;
    setFrameProgress?: (progress: number) => void;
    // Splat API
    /** Switch back to the original/initial splat */
    goToOriginalSplat: () => void;
    /**
     * Switch to a splat by URL: a splat swap (its `additionalSplats[].url`), the original splat
     * (ORIGINAL_SPLAT_URL or any file of it: uploaded file, SOG, LOD manifest, mobile set), or any
     * other splat file, which loads placed like the original.
     */
    goToSplat: (url: string) => Promise<void>;
    /** Get the URL of the currently displayed splat */
    getCurrentSplatUrl: () => string;
    /** Check if currently showing the original splat */
    isShowingOriginalSplat: () => boolean;
    /** Get list of additional splats available for swapping */
    getAdditionalSplats: () => Array<{
        url: string;
        name?: string;
        waypointIndex: number;
        percentage: number;
    }>;
    // Compare mode API
    /** Whether the viewer is in compare (before/after) mode */
    isCompareMode: () => boolean;
    /** Get current compare slider position (0-1) */
    getComparePosition: () => number;
    /** Set compare slider position programmatically (0-1) */
    setComparePosition: (position: number) => void;
    // Lifecycle
    destroy: () => void;
    resize: () => void;
    // Portal navigation
    navigateToScene: (sceneId: string) => Promise<void>;
    // Mode
    setCameraMode: (mode: 'tour' | 'explore' | 'walk') => void;
    getCameraMode: () => string;
    /**
     * Hidden FPV drone flight mode. Lets the viewer be flown through the scene
     * like an FPV quadcopter (keyboard or game controller). Not part of the
     * normal UI — usually reached by typing "fpv" or a gamepad chord (both
     * bumpers + Start) — but exposed here for programmatic control.
     */
    enterFpvDroneMode?: () => void;
    exitFpvDroneMode?: () => void;
    toggleFpvDroneMode?: () => void;
    isFpvDroneMode?: () => boolean;
    /**
     * Toggle the performance metrics HUD (also bound to F3 / Ctrl+Shift+M, or forced with
     * ?perfhud=1). No-op when the scene sets uiOptions.perfHudEnabled = false.
     */
    togglePerfHud: (visible?: boolean) => void;
    /**
     * Performance mode: halves the render resolution cap and the splat budget for slower
     * devices. Persisted per browser in localStorage (`storysplat.performanceMode`) and
     * forceable with `?perf=1` / `?perf=0`. Also exposed as a checkbox in the help panel.
     */
    setPerformanceMode: (enabled: boolean) => void;
    getPerformanceMode: () => boolean;
    /**
     * Render one more frame. Only needed under on-demand rendering, and only for changes the
     * viewer cannot observe on its own — the camera, node transforms, scripts, playing videos and
     * GIFs, streaming and input are all detected automatically. Reach for this after mutating
     * something invisible to those probes, such as a material colour on an entity you own.
     */
    requestRender: (reason?: string) => void;
    /**
     * On-demand rendering: when active the viewer renders only when something on screen can have
     * changed (plus a 1 Hz heartbeat). Starts automatically after the initial reveal for scenes
     * without self-animating features; `setOnDemandRendering(false)` renders every frame.
     */
    setOnDemandRendering: (enabled: boolean) => void;
    getOnDemandRendering: () => boolean;
    /** Diagnostics: whether on-demand rendering is active, why a scene stays continuous, and frame counters. */
    getOnDemandRenderStats: () => ViewerOnDemandRenderStats | null;
    /**
     * Shadow state for diagnostics: tier ('full' | 'reduced' | 'off'), whether shadows are on,
     * splat casting / receiving, how many times a shadow map was re-rendered, and per light
     * whether it casts, its map size and the size of the area its map covers.
     */
    getShadowDebugInfo?: () => ViewerShadowDebugInfo | null;
    /** Cloud layers: tier, splats per layer, generation time, on-screen / animating state (diagnostics). */
    getCloudDebugInfo?: () => Record<string, unknown> | null;
    /** Scene wind: the wind now, its clock, and each wind-reactive object / area (diagnostics). */
    getWindDebugInfo?: () => Record<string, unknown> | null;
    /** Diagnostics: hold the wind clock at `seconds` (null lets it run again). */
    setWindClock?: (seconds: number | null) => void;
    /**
     * Resume tour mode using the camera's current pose as the starting point.
     * Snaps tour progress to the nearest point on the waypoint path so the
     * camera doesn't jump back to the previously paused progress.
     */
    resumeTourAtCurrentCamera: () => void;
    setExploreMode: (mode: 'orbit' | 'fly' | 'walk') => void;
    /** Update runtime explore sub-mode defaults without rebuilding the viewer. */
    setExploreModeSettings: (settings: {
        enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'> | null;
        initialSplatExploreMode?: 'orbit' | 'fly' | 'walk' | null;
        applyCurrent?: boolean;
    }) => void;
    // Progress (direct tour progress control)
    setProgress: (progress: number, animate?: boolean) => void;
    getProgress: () => number;
    /** Update the easing used by manual animated waypoint jumps. */
    setWaypointEasing: (mode: WaypointEasingMode) => void;
    setPathInterpolation: (mode: 'smooth' | 'linear') => void;
    // Audio
    muteAll: () => void;
    unmuteAll: () => void;
    isMuted: () => boolean;
    // Hotspot interaction
    getHotspots: () => Array<{
        id: string;
        title?: string;
        type: string;
        position: {
            x: number;
            y: number;
            z: number;
        };
    }>;
    /** Open a hotspot as if clicked: its popup, the hotspotClick event and its linked actions. */
    triggerHotspot: (id: string) => void;
    closeHotspot: () => void;
    /**
     * Run one linked action (e.g. `{ id: 'a', target: { kind: 'hotspot', id: 'sign' }, action: 'playVideo' }`).
     * Resolves once the action settled (video play attempted, with a muted retry if the
     * browser blocked sound). A missing target or an action the target can't perform is skipped.
     */
    runLinkedAction: (action: LinkedAction) => Promise<void>;
    /** Show an overlay by id regardless of its triggers. Returns false for an unknown id. */
    showOverlay: (id: string) => boolean;
    /** Hide an overlay by id; a still-true trigger re-shows it only after leaving and re-entering. */
    hideOverlay: (id: string) => boolean;
    /** Ids of the overlays currently on screen. */
    getVisibleOverlays: () => string[];
    /**
     * Regions: switch one region on or off by id (it fades over 250 ms). Like a visitor's Layers
     * menu click, this overrides its triggers until they next change it. In the Layers menu's
     * 'single' mode, showing a menu region hides the other menu regions. False for an unknown id.
     */
    setRegionVisible: (id: string, visible: boolean) => boolean;
    /** Show only this region and hide every other one (null hides them all). Returns false for an unknown id. */
    showOnlyRegion: (id: string | null) => boolean;
    /** Ids of the regions currently switched on, in region order. */
    getVisibleRegions: () => string[];
    /** The scene's drawable regions with their current state; `area` is the sum of their outlines' areas (scene units²). */
    getRegions: () => RegionInfo[];
    /**
     * Legacy alias. 'focus': setRegionVisible for that region (focus regions became regions that
     * dim the rest). 'haze': switch a triggered haze region on or off regardless of its triggers
     * (haze regions without triggers are always on). Returns false for an unknown id.
     */
    setRegionActive: (kind: 'focus' | 'haze', id: string, active: boolean) => boolean;
    /** Legacy alias. `focus`: the visible regions that dim the rest; `haze`: the haze regions applied. */
    getActiveRegions: () => { focus: string[]; haze: string[] };
    /**
     * Legacy alias for layers: the regions in the Layers menu, plus colour-only regions (what
     * annotation layers became, even with the menu hidden). Show or hide one by id; in 'single'
     * menu mode showing a menu region hides the others. Returns false for an unknown id.
     */
    setLayerGroupVisible: (id: string, visible: boolean) => boolean;
    /** Legacy alias: show only this layer among the layers (null hides every layer). Returns false for an unknown id. */
    showOnlyLayerGroup: (id: string | null) => boolean;
    /** Legacy alias: show (true) or hide (false) every layer. */
    setAllLayerGroupsVisible: (visible: boolean) => void;
    /** Legacy alias: ids of the layers currently shown. */
    getVisibleLayerGroups: () => string[];
    /** Legacy alias: the layers with their current visibility and total area (scene units²). */
    getLayerGroups: () => Array<{ id: string; name: string; color: string; section?: string; visible: boolean; area: number }>;
    /**
     * Measurement sets (the scene's `measurementSets`): each with whether its saved measurements
     * show and how many it holds, in order, plus `{ id: 'other', name: 'Other measurements' }` when
     * some saved measurements belong to no set. Empty when the scene has no sets, no measurement
     * tool, or doesn't ship its saved measurements to the viewer.
     */
    getMeasurementSets: () => MeasurementSetInfo[];
    /**
     * Show or hide one measurement set's saved measurements, like ticking it in the Measure panel
     * ('other' = the measurements in no set). Visitors' own measurements are not affected.
     * Returns false for an unknown id.
     */
    setMeasurementSetVisible: (id: string, visible: boolean) => boolean;
    /** Show (true) or hide (false) every measurement set, like "Show all measurements". */
    setAllMeasurementSetsVisible: (visible: boolean) => void;
    /**
     * Hotspot folders: show or hide a folder's hotspots and those of every folder inside it
     * (markers and leader lines; an open popup of a hidden one closes). Works whether or not the
     * visitor directory is on; its tick boxes follow. A hotspot its own settings hide stays hidden.
     * Returns false for an unknown id.
     */
    setHotspotFolderVisible: (id: string, visible: boolean) => boolean;
    /** Show only this hotspot folder and the folders inside it (null hides every folder). Hotspots not in a folder always show. False for an unknown id. */
    showOnlyHotspotFolder: (id: string | null) => boolean;
    /** Ids of the hotspot folders shown (their own hotspots), in tree order. */
    getVisibleHotspotFolders: () => string[];
    /** Every hotspot folder in tree order (empty ones too) with its parent, its visibility, and its own hotspots' and views' ids in order. */
    getHotspotFolders: () => HotspotFolderInfo[];
    /** What the visitor directory lists (empty folders and views hidden from it left out) and whether it is on and open. */
    getDirectory: () => VisitorDirectoryInfo;
    /** Open the visitor directory. False when the scene has none (switched off, nothing to list, or no viewer UI). */
    openDirectory: () => boolean;
    closeDirectory: () => void;
    // Panorama mode (360° skybox-only view)
    /** Transition to/from panorama mode — fades splat and locks camera to rotation-only */
    setPanoramaMode: (enabled: boolean) => void;
    /** Whether panorama mode is currently active */
    isPanoramaMode: () => boolean;
    // UI customization
    setButtonLabels: (labels: Partial<ViewerButtonLabels>) => void;
    // Events
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on: (event: ViewerEvent, callback: (...args: any[]) => void) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    off: (event: ViewerEvent, callback: (...args: any[]) => void) => void;
    // Internal observer API — registers a listener that doesn't count as an
    // external consumer. Used for analytics/forwarding so e.g. portal
    // navigation doesn't wrongly detect an override handler. Not part of the
    // public SDK; consumers should keep using on/off.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInternal?: (event: ViewerEvent, callback: (...args: any[]) => void) => void;
}
/**
 * Viewer options for createViewer
 */
export interface ViewerOptions {
    /** Runtime-only compatibility mode. Does not change the saved scene. */
    disableWebGPU?: boolean;
    /**
     * Where this page serves the 8th Wall AR engine files (xr.js, xr-slam.js, LICENSE), e.g.
     * 'https://cdn.example.com/8thwall'. Set by the host page; a scene's own value is only used
     * when it is a same-site path. Defaults to /cdn/8thwall on the page's own site.
     */
    eighthWallBaseUrl?: string;
    /**
     * Offer VR on this page. Unset: headsets (Quest, Pico, Apple Vision Pro) are offered VR in
     * every scene; desktops (PC VR) and phones when the page URL has `?vr=1`, the visitor turned
     * on VR mode (setVrMode), or the scene turned XR on with VR. `true` offers it on every device
     * with WebXR and shows the VR button as a large "Enter VR" prompt; `false` offers it nowhere.
     */
    vr?: boolean;
    /** Editor hosts rebuild their canvas tools from current state. Return false
     * while active splat edits must be saved before replacing the renderer. */
    onGraphicsRecovery?: (reason: string) => boolean | Promise<boolean>;
    template?: 'standard' | 'minimal' | 'pro';
    autoPlay?: boolean;
    showUI?: boolean;
    backgroundColor?: string;
    /**
     * @internal Short-lived StorySplat asset lease context returned by the
     * trusted scene API. Renewal endpoints are restricted to same-origin or
     * the fixed production Discover endpoint.
     */
    assetLease?: {
        sceneId: string;
        ownerId: string;
        expiresAtMs: number;
        renewalEndpoint?: string;
    };
    /**
     * Portal teleport navigation for embeds / the v2-html iframe. When set, a
     * portal teleport does an IN-PLACE scene swap (keeping the JS context — and
     * therefore the cross-scene ambient audio bed — alive) and updates the URL
     * via history.pushState so deep-links and the browser back button work,
     * WITHOUT a full page reload. A full reload (window.location.assign to
     * baseUrl+sceneId) happens ONLY when the target scene pins a viewer version
     * that DIFFERS from `runtimeVersion` (the version this document is already
     * running) — that's the one case a bundle swap is unavoidable. A story chain
     * pinned to a single version therefore teleports in place and keeps the bed.
     */
    portalNavigation?: {
        /** e.g. https://discover.storysplat.com/api/v2-html/ */
        baseUrl: string;
        /** appended after the scene id, e.g. "?embed=1" */
        query?: string;
        /**
         * The exact viewer version THIS document is running (the bundle the host
         * route served). A portal target that pins a different version needs a
         * full reload to load that bundle; one that matches (or any target, when
         * this is omitted — i.e. a floating/always-latest runtime such as local
         * dev or a non-pinned embed) swaps in place. Omit for floating runtimes.
         */
        runtimeVersion?: string | null;
    };
    /** Reveal effect preset for splat loading animation. Use 'none' to disable. Default: 'medium' */
    revealEffect?: 'fast' | 'medium' | 'slow' | 'none';
    /** Reveal effect style: 'bloom' (fast burst with overshoot) or 'radial' (classic dot wave). Default: 'bloom' */
    revealStyle?: 'bloom' | 'radial';
    /** Enable lazy loading - shows a thumbnail with start button before loading the viewer */
    lazyLoad?: boolean;
    /** Custom thumbnail URL for lazy loading. If not provided, uses scene's thumbnailUrl. Supports image, video, or GIF */
    lazyLoadThumbnail?: string;
    /**
     * Type of media for the lazy load thumbnail.
     * - 'image': Static image (jpg, png, webp)
     * - 'video': Video file (mp4, webm) - will autoplay muted and loop
     * - 'gif': Animated GIF
     * If not specified, auto-detects from URL extension
     */
    lazyLoadThumbnailType?: 'image' | 'video' | 'gif';
    /** Custom start button text for lazy loading. Default: "Start Experience" */
    lazyLoadButtonText?: string;
    /** Allow parent page styles to leak into the viewer. By default (false), viewer styles are isolated. */
    allowParentStyles?: boolean;
    /**
     * Enable editor mode. When true, createViewer returns an EditorViewerInstance
     * with additional mutation methods for scene editing. Default: false.
     * Non-editor consumers are completely unaffected.
     */
    editor?: boolean;
    /**
     * When editor: true, skip creating viewer UI (preloader, scroll controls, etc.)
     * since the editor has its own UI. Default: false.
     */
    editorSkipUI?: boolean;
    /**
     * Enable analytics tracking. When set, the viewer records session data
     * (camera movement, waypoint changes, hotspot clicks, etc.) and sends
     * it to the StorySplat analytics API.
     */
    analytics?: {
        sceneId: string;
        ownerId: string;
        /** Base URL for the analytics API. Defaults to 'https://discover.storysplat.com' */
        baseUrl?: string;
        /**
         * When true, the streaming bandwidth meter only reports if the page is
         * served from a StorySplat host. Set by generateHTML so a downloaded,
         * self-hosted export opened on a customer domain / file:// doesn't phone
         * home. Live embeds (createViewerFromSceneId) leave this unset and always
         * report — that CDN egress is real and billable.
         */
        onlyReportBandwidthOnStorySplatHost?: boolean;
        /**
         * When true, all StorySplat telemetry is disabled outside StorySplat
         * hosts. Generated standalone HTML sets this so self-hosted copies do
         * not create visitor ids or phone home by default.
         */
        onlyReportAnalyticsOnStorySplatHost?: boolean;
    };
    /**
     * CDN proxy host (e.g. 'my-proxy.workers.dev/story-splat') that fronts Firebase Storage. When the
     * deployed worker host differs from the built-in default, pass it here so the viewer still
     * recognizes proxied URLs as StorySplat-hosted and attributes their bandwidth — otherwise a
     * changed host makes every proxied scene report bandwidthUsed: 0 as if self-hosted. Editor and
     * discover configure the proxy host from an env var; this keeps the viewer in sync.
     */
    cdnProxyHost?: string;
    /**
     * Endpoint the "Ask this scene" guide (uiOptions.sceneGuide) posts visitor questions to; it
     * answers as a text/event-stream. Defaults to the StorySplat sceneGuide Cloud Function.
     */
    sceneGuideEndpoint?: string;
    /**
     * Disable all analytics tracking, including StorySplat session tracking
     * and optional Google Analytics events.
     */
    disableAnalytics?: boolean;
    /**
     * Google Analytics Measurement ID, for example G-XXXXXXXXXX.
     * When provided, the viewer sends interaction events to the embedder's
     * GA property.
     */
    googleTagId?: string;
    /**
     * Optional hosted ZSTD decoder URLs for SPZ loading. When omitted, the
     * viewer uses its embedded decoder, so self-hosted exports need no extra files.
     * An existing PlayCanvas ZstdDecoderModule configuration always takes precedence.
     */
    zstdWasm?: {
        glueUrl: string;
        wasmUrl: string;
    };
    /**
     * Version string of the host application embedding the viewer (e.g. the
     * StorySplat editor's release version). Surfaced in the F3 performance HUD
     * so a diagnostics copy shows which editor build produced/opened the scene.
     * Optional — standalone published scenes leave it unset.
     */
    appVersion?: string;
}
/**
 * Extended viewer instance returned when editor: true.
 * Includes mutation methods for scene editing.
 */
export interface EditorViewerInstance extends ViewerInstance {
    // Camera
    setCameraMode: (mode: 'tour' | 'explore' | 'walk') => void;
    getCameraMode: () => string;
    getCameraControls: () => unknown; // CameraControls instance
    /** Update live camera movement/rotation/walk settings without rebuilding the viewer. */
    setCameraControlSettings: (settings: CameraControlSettings) => void;
    /** Update the runtime orbit pose/limit settings without rebuilding the viewer. */
    setOrbitCameraSettings?: (settings: OrbitCameraSettingsConfig | null) => void;
    /** Suppress all camera input (CameraControls + tour rotation). Call during gizmo drag. */
    suppressInput: () => void;
    /** Resume camera input after gizmo drag. */
    resumeInput: () => void;
    /**
     * Enter/exit video-render capture mode. When active, the autonomous
     * per-frame camera drivers (tour damping, walk character controller) are
     * frozen so the render's pinned pose is the only thing writing the camera.
     * Leaving it puts back the splat (and skybox) that was on screen when the
     * render's first prepareCaptureFrame ran.
     */
    setRenderCaptureMode: (active: boolean) => void;
    /**
     * Video render (call while setRenderCaptureMode is on, once per output frame, before
     * rendering it): apply the splat swaps the PUBLISHED scene would show for this frame —
     * proximity areas at `pose`, and waypoint / percentage swaps at the tour timeline
     * `progress` (as passed to getPathPoseAtProgress; leave it out off the tour) — advance
     * swap transitions by `dtSec` (1 / fps), and resolve once every swap load this started
     * has finished. The first frame shows its state without a transition.
     */
    prepareCaptureFrame?: (frame: { pose: CameraPose; progress?: number; dtSec: number }) => Promise<void>;
    /** Enable/disable the surface reticle (e.g. hide during gizmo editing). */
    setReticleEnabled: (enabled: boolean) => void;
    /**
     * TDR-safe CPU screen-point pick (no GPU gsplat pass). Nearest real-collision hit or null.
     * `seeThroughHaze` (measurements, area outlines): look through large, partly transparent splats
     * floating in front of the surface, as the viewer's own measurement tool does.
     */
    raycastScreenPoint?: (screenX: number, screenY: number, options?: { seeThroughHaze?: boolean }) => { x: number; y: number; z: number } | null;
    /**
     * The splat object drawn under a canvas point (CSS pixels): 'main-splat' for the scan, a custom
     * mesh id for a splat model, or null. CPU only (splat centres near the point, nearest wins).
     */
    pickSplatObjectAtScreen?: (screenX: number, screenY: number) => string | null;
    /**
     * The box around splat objects, in editor scene coordinates (Z-mirrored, like focus regions):
     * what a "Splat object" selection stores for viewers that predate it. Null when none is loaded.
     */
    getSplatObjectBounds?: (ids: string[]) => { position: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } } | null;
    /**
     * The splat objects loaded right now (each has at least one gsplat): 'main-splat' and custom
     * mesh ids, including GLB models that turned out to hold KHR_gaussian_splatting splats.
     */
    getSplatObjectIds?: () => string[];
    /** Override reticle color (e.g. orange for measurement mode). Pass null to reset to white. */
    setReticleColor: (color: { r: number; g: number; b: number } | null) => void;
    /** Set the hotspot currently being edited — suppresses popup for that hotspot. Pass null to clear. */
    setEditingHotspotId: (id: string | null) => void;
    /**
     * Preview an element's CutawayAction in the editor viewport, which doesn't run them on its own
     * (it has no Cutaway button to clear a cut). The plane slides as visitors see it; null clears
     * the cut at once.
     */
    previewCutaway?: (action: CutawayAction | null) => void;
    /** Update refocus tap mode at runtime ('single', 'double', or 'none' to disable) */
    setRefocusTapMode: (mode: 'single' | 'double' | 'none') => void;
    /** Switch the splat-derived collision representation queried at runtime ('voxel' default, or 'mesh'). */
    setCollisionSystem: (system: 'voxel' | 'mesh') => void;
    /** Master toggle for splat-derived collision (voxel + mesh). Custom primitives are unaffected. */
    setSplatCollisionEnabled: (enabled: boolean) => void;
    /** Late-load or clear the voxel octree collision data. */
    setVoxelCollisionUrl: (url: string | null) => Promise<void>;
    /** Late-load the collision GLB (e.g. after the user just generated it or fused LCC2 meshes). */
    setCollisionMeshUrl: (url: string | null) => Promise<void>;
    // Scene mutation - Hotspots
    addHotspot: (hotspot: HotspotData) => string;
    removeHotspot: (id: string) => void;
    updateHotspot: (id: string, data: Partial<HotspotData>) => void;
    /**
     * Multiplier a "keep same size on screen" hotspot currently has in its local scale (1 when the
     * mode is off). It is held while an editor gizmo drags, so a dragged scale divided by it is the
     * new set size.
     */
    getHotspotScreenSizeFactor: (id: string) => number;
    /** Current distance multiplier, held during a gizmo drag; divide it out before saving scale. */
    getPortalScreenSizeFactor: (id: string) => number;
    // Scene mutation - Lights
    addLight: (light: LightConfig) => string;
    removeLight: (id: string) => void;
    updateLight: (id: string, data: Partial<LightConfig>) => void;
    // Scene mutation - Custom Meshes
    addCustomMesh: (mesh: CustomMeshConfig) => string;
    removeCustomMesh: (id: string) => void;
    updateCustomMesh: (id: string, data: CustomMeshUpdateData) => void;
    // Scene mutation - Particles
    addParticleSystem: (ps: ParticleConfig) => string;
    removeParticleSystem: (id: string) => void;
    // Scene mutation - Portals
    addPortal: (portal: PortalData) => string;
    removePortal: (id: string) => void;
    updatePortal: (id: string, data: Partial<PortalData>) => void;
    // Scene mutation - HTML Meshes
    addHtmlMesh: (config: HtmlMeshConfig) => string;
    removeHtmlMesh: (id: string) => void;
    /** Change a live HTML mesh in place (see HtmlMeshUpdateData); other fields need remove + add. */
    updateHtmlMesh: (id: string, data: HtmlMeshUpdateData) => void;
    // Scene mutation - Collision Meshes
    /**
     * Add a live collider; `visible: false` hides it but it still collides. The first add or
     * remove replaces the colliders built from the scene's `collisionMeshesData` at load: from then
     * on the live colliders are the scene's collision, so add every collider the scene has.
     */
    addCollisionMesh: (config: CollisionMeshConfig) => string;
    removeCollisionMesh: (id: string) => void;
    // Scene mutation - Audio Emitters
    addAudioEmitter: (config: AudioEmitterConfig) => string;
    removeAudioEmitter: (id: string) => void;
    updateAudioEmitter: (id: string, data: Partial<AudioEmitterConfig>) => void;
    // Scene mutation - Splat Transform
    setSplatScale: (scale: number, invertX?: boolean, invertY?: boolean) => void;
    setSplatPosition: (x: number, y: number, z: number) => void;
    setSplatRotation: (x: number, y: number, z: number) => void;
    /**
     * The main splat's current transform in the values setSplatPosition / setSplatRotation
     * (radians) / setSplatScale take, after the editor's gizmo moved its entity directly.
     */
    readSplatTransform: () => { position: [number, number, number]; rotation: [number, number, number]; scale: number } | null;
    /**
     * World bounds of an entity as it renders (splats from their centres, ignoring far
     * strays; meshes from their mesh instances), for framing it. Null when nothing renders.
     */
    getEntityBounds: (entity: pc.Entity) => { center: [number, number, number]; radius: number } | null;
    /**
     * Editor-only hiding (the scene outliner's eye): the entity found under `source` stays
     * disabled in the editor view, whatever else toggles it, until shown again; it is
     * looked up again every frame, so a recreated entity stays hidden. Nothing is saved.
     * `map` names one of the getAll*Entities maps, or 'mainSplat'.
     */
    setEditorHidden: (source: { map: string; id: string }, hidden: boolean) => void;
    // Scene mutation - Playback
    setAutoplaySpeed: (speed: number) => void;
    setLoopMode: (mode: 'loop' | 'pingpong' | 'none') => void;
    setScrollSpeed: (speed: number) => void;
    // Scene mutation - Environment
    setSkybox: (url: string | null, rotation?: number, verticalOffset?: number) => void;
    setSkyboxRotation: (rotation: number) => void;
    setSkyboxVerticalOffset: (offset: number) => void;
    setBackgroundColor: (color: {
        r: number;
        g: number;
        b: number;
    }) => void;
    setFOV: (fov: number) => void;
    // Waypoints
    addWaypoint: (wp: WaypointData) => void;
    removeWaypoint: (index: number) => void;
    updateWaypoint: (index: number, data: Partial<WaypointData>) => void;
    rebuildTourPath: (waypoints?: WaypointData[]) => void;
    /** Replace the scene's extra waypoints (what goToExtraWaypoint and hotspot clicks resolve against). */
    setExtraWaypoints: (extraWaypoints: ExtraWaypointData[]) => void;
    // Splat loading (for editor use - load/replace the primary splat after init)
    loadSplatUrl: (splatUrl: string, sogUrl?: string, ...fallbackUrls: Array<string | undefined>) => Promise<void>;
    /**
     * Resolves once the splat from the most recent loadSplatUrl() has streamed to "decent" detail —
     * the same reveal condition (and cap, ~8s) the published viewer holds its preloader for. Resolves
     * immediately for non-LOD / mesh loads and after a failed load. Never rejects, never hangs.
     */
    whenRevealReady: () => Promise<void>;
    // Scene mutation - Splat Relighting
    setSplatRelighting: (config: Partial<SplatRelightingConfig>) => void;
    /** Set relighting fade value (0 = lights off, 1 = lights on) without changing the enabled state */
    setRelightFade: (fade: number) => void;
    /** Update narration track at runtime (editor uploads audio after viewer init) */
    setNarrationTrack: (track: { audioUrl: string; volume?: number; backgroundMusicUrl?: string; backgroundMusicVolume?: number; segments: NarrationSegmentConfig[]; enabled: boolean } | null) => void;
    // Scene access
    getApp: () => pc.Application;
    getSplatEntity: () => pc.Entity | null;
    /**
     * Where the primary splat's data would be drawn if it were saved as a plain
     * PLY (the Babylon-era Y/Z flip plus the scene's splat transform), and
     * whether the current source uses the native (LCC2 / SPZ) frame instead.
     * The splat editor bakes `inverse(plyWorldTransform) · currentWorld` into
     * saved data so an edited LCC2/SPZ/GLB splat reloads exactly in place.
     */
    getPrimarySplatFrame: () => { nativeFrame: boolean; plyWorldTransform: Float32Array; sceneContentWorldTransform: Float32Array };
    getSwapSplatEntity: (url: string) => pc.Entity | null;
    ensureSwapSplatLoaded: (url: string, splatOverrides?: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }) => Promise<pc.Entity | null>;
    getAllHotspotEntities: () => Map<string, pc.Entity>;
    getAllLightEntities: () => Map<string, pc.Entity>;
    getAllPortalEntities: () => Map<string, pc.Entity>;
    getAllCustomMeshEntities: () => Map<string, pc.Entity>;
    getAllParticleEntities: () => Map<string, pc.Entity>;
    getAllCollisionMeshEntities: () => Map<string, pc.Entity>;
    getAllHtmlMeshEntities: () => Map<string, pc.Entity>;
    getCamera: () => pc.Entity;
    // Progress
    setProgress: (progress: number, animate?: boolean) => void;
    getProgress: () => number;
    // Transition speed
    setTransitionSpeed: (speed: number) => void;
    // Splat swap
    updateAdditionalSplats: (splats: SplatSwapPoint[]) => void;
    setSwapTransitionEnabled: (enabled: boolean) => void;
    /** Override the primary splat URL used by the swap system to revert to the original splat. */
    setPrimarySplatUrl: (url: string) => void;
    // Compare mode
    /** Enable compare mode with a second splat URL (loads it and attaches clip shaders) */
    /**
     * Turn on split compare against `afterSplatUrl`.
     *
     * Pass the whole `SplatSwapPoint` as `splatOverrides` where you have it: the
     * after splat is resolved from it, and only there will its `lodMetaUrl` be
     * seen — which is what lets the after side stream its own LODs. A bare
     * transform still works, it just loads whatever `afterSplatUrl` points at.
     */
    enableCompareMode: (afterSplatUrl: string, settings?: Partial<CompareConfig>, splatOverrides?: Partial<SplatSwapPoint>) => Promise<void>;
    /** Update transform of a swap/compare splat entity at runtime */
    updateSwapSplatTransform: (url: string, transform: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: { x: number; y: number; z: number } }) => void;
    /** Disable compare mode (removes clip shaders and hides compare splat) */
    disableCompareMode: () => void;
    /** Update compare mode settings at runtime */
    setCompareSettings: (settings: Partial<CompareConfig>) => void;
    // Playground mode (light ball demo)
    startPlayground: () => void;
    stopPlayground: () => void;
    isPlaygroundActive: () => boolean;
    // Voxel collision
    initVoxelCollision: (jsonUrl: string) => Promise<void>;
    // Voxel collision debug visualization
    setVoxelDebug: (visible: boolean) => void;
    getVoxelDebugVisible: () => boolean;
    clearVoxelDebug: () => void;
    /** Get the dedicated VoxelDebug layer ID for rendering overlays above gsplats */
    getVoxelDebugLayerId: () => number;
    // Entity animations
    getEntityAnimationSystem: () => import('../dynamic-viewer/EntityAnimationSystem').EntityAnimationSystem | null;
    setEntityAnimations: (animations: EntityAnimationConfig[]) => void;
    // LOD settings
    setLodSettings: (config: LodSettingsConfig) => void;
    // Post-processing
    setPostProcessing: (config: PostProcessingConfig | undefined) => void;
    /** Replace the overlay set for live editing; resets trigger state. */
    setOverlays: (overlays: OverlayData[] | undefined) => void;
    /**
     * Editor-only proxy for a scene-anchored region (focus, haze, overlay trigger). The entity
     * is parented to the scene root so the transform gizmo can move, rotate and scale it; read
     * the result back with readRegionProxy(). Pass null to remove it. One proxy at a time.
     */
    setRegionProxy: (shape: RegionShape | null) => pc.Entity | null;
    getRegionProxyEntity: () => pc.Entity | null;
    /**
     * Replace the regions for live editing (undefined: none). Recompiles the splat shader only when
     * areas change; switching, recolouring and moving sphere / box areas are uniform writes. From
     * the first call on, the legacy `postProcessing.focusRegions` / `annotationLayers` are ignored.
     */
    setRegions: (config: RegionsConfig | undefined) => void;
    /**
     * Keep one region (or haze region) drawn whatever its state or triggers say (the one being
     * edited in the viewport), so the creator sees what they are shaping. Pass null to let every
     * region follow its state again. `kind: 'focus'` is accepted as an alias of 'region'.
     */
    setRegionPreview: (target: { kind: 'region' | 'haze' | 'focus'; id: string } | null) => void;
    /**
     * Editor-only line overlay for region areas and area measurements (scene coordinates, the space
     * raycastScreenPoint returns). `outlines` draws every area of a drawn region in its colour:
     * outline lines, sphere / box wireframes, splat-object boxes (other regions dimmed while
     * `selectedRegionId` is open), instead of the effect's own bounds. The area `selectedAreaId` is
     * drawn brighter and thicker (outlines with their points marked), with `selectedAreaPoints` when given (the
     * editor's live copy while a point is dragged, before the debounced setRegions lands); `draft`
     * is the outline being clicked (open polyline, closed back to its first point once it has
     * three). `areas` are area measurements to outline. `regionOutline` is a haze outline being
     * edited in the viewport (its points marked).
     */
    setRegionEditorOverlay: (overlay: RegionEditorOverlay | null) => void;
    /** The proxy's current transform in editor scene coordinates (position, rotation in degrees, full size). */
    readRegionProxy: () => Pick<RegionShape, 'position' | 'rotation' | 'size'> | null;
    /** Camera position in editor scene coordinates: the frame focus, haze and overlay regions are authored in. */
    getCameraScenePosition: () => { x: number; y: number; z: number };
    /**
     * A point in editor scene coordinates (as getCameraScenePosition returns) in editor world
     * coordinates, the frame setCameraView-style helpers take. Differs when the scene is moved,
     * rotated or scaled.
     */
    scenePositionToWorld: (point: { x: number; y: number; z: number }) => { x: number; y: number; z: number };
    /**
     * Legacy (editors and embedders that predate Regions): replace the annotation layers. They are
     * converted to regions (regionsFromLegacy, with the focus regions) until setRegions is called,
     * unless the scene has `regions`.
     */
    setAnnotationLayers: (config: AnnotationLayersConfig | undefined) => void;
    /** Legacy alias: keep this (converted) layer's region drawn, in its own preview slot. Null releases it. */
    setAnnotationLayerPreview: (id: string | null) => void;
    /**
     * Legacy alias of setRegionEditorOverlay (`selectedShapeId` / `selectedShapePoints` are the
     * selected area and its live points).
     */
    setAnnotationEditorOverlay: (overlay: {
        outlines?: boolean;
        selectedShapeId?: string | null;
        selectedShapePoints?: Array<{ x: number; y: number; z: number }> | null;
        draft?: Array<{ x: number; y: number; z: number }> | null;
        draftColor?: string;
        areas?: Array<{ points: Array<{ x: number; y: number; z: number }>; color?: string }>;
        regionOutline?: { points: Array<{ x: number; y: number; z: number }>; color?: string } | null;
    } | null) => void;
    /** Fisheye / tiny-planet projection amount, 0 to 1. Use 0 to disable. (PlayCanvas 2.18+) */
    setFisheye: (amount: number) => void;
    /** Enable/configure procedural splat weather. Pass undefined or { enabled: false } to disable. (PlayCanvas 2.18+) */
    setWeather: (config: WeatherConfig | undefined) => void;
    /** Enable/configure the audio-reactive splat effect. Pass undefined or { enabled: false } to disable. */
    setAudioReactive: (config: AudioReactiveConfig | undefined) => void;
    // Segment data
    loadSegments: (dataUrl: string, metaUrl?: string) => Promise<void>;
    clearSegments: () => void;
    getSegmentMetadata: () => import('../segments/SegmentAttachment').SegmentMetadata | null;
    getSegmentIds: () => Uint16Array | null;
    selectBySegment: (segmentId: number, editAttachmentState?: Uint8Array, updateFn?: () => void) => void;
    // Scene mutation - Mirror Planes
    addMirrorPlane: (data: MirrorPlaneData) => string;
    removeMirrorPlane: (id: string) => void;
    updateMirrorPlane: (id: string, data: Partial<MirrorPlaneData>) => void;
    getAllMirrorPlaneEntities: () => Map<string, pc.Entity>;
    // Scene mutation - Water Planes
    addWaterPlane: (data: WaterPlaneData) => string;
    removeWaterPlane: (id: string) => void;
    updateWaterPlane: (id: string, data: Partial<WaterPlaneData>) => void;
    getAllWaterPlaneEntities: () => Map<string, pc.Entity>;
    /** Water quality tier this device renders: 'high' (desktop), 'low' (phones / performance mode), 'opaque' (XR). */
    getWaterQualityTier?: () => 'high' | 'low' | 'opaque';
    // Scene mutation - Cloud layers
    addCloudLayer: (data: CloudLayerData) => string;
    removeCloudLayer: (id: string) => void;
    /** In place: look fields are uniforms; preset / scale / seed / quality / area regenerate after edits settle. */
    updateCloudLayer: (id: string, data: Partial<CloudLayerData>) => void;
    getAllCloudLayerEntities: () => Map<string, pc.Entity>;
    /** Outline one layer's box (or dome) in the viewport, or none. */
    setCloudLayerBoundsVisible?: (id: string | null) => void;
    /** The scene's entity animations, for cloud layers whose box size is keyframed. */
    setCloudAnimations?: (animations: EntityAnimationConfig[] | undefined) => void;
    /** Cloud quality tier this device renders: 'high' (desktop), 'medium' (performance mode), 'low' (phones, XR). */
    getCloudQualityTier?: () => 'high' | 'medium' | 'low';
    /** A new layer's placement and size for this scan: above its objects, inside its baked sky (keep) or high above (replace). */
    suggestCloudLayer?: (preset?: CloudPreset) => Pick<CloudLayerData, 'area' | 'position' | 'rotation' | 'scale' | 'cloudScale'>;
    /** Apply the scan-sky settings in place. */
    setScanSky?: (settings: ScanSkySettings | undefined) => void;
    /** Editor preview: tint the splats 'replace' would remove (magenta) instead of removing them. */
    setScanSkyPreview?: (on: boolean) => void;
    /** Apply the scene wind (scene field `wind`, wind-reactive areas included) in place. */
    setSceneWind?: (settings: SceneWindSettings | undefined) => void;
    /** Outline one wind-reactive area in the viewport, or none. */
    setWindAreaBoundsVisible?: (id: string | null) => void;
    /** What was measured: the objects around the capture point and the baked sky's distance (scene units). */
    getScanSkyInfo?: () => { available: boolean; keepRadius?: number; shellDistance?: number | null; skyCoverage?: number; objectsTop?: number; ground?: number } | null;
    // Proximity radius visualization
    /** Show a visualization around an entity: trigger radius (blue) + optional spatial audio ranges (orange = max distance, green = ref distance) */
    showProximityRadius: (entity: pc.Entity, radius: number, audioConfig?: { maxDistance?: number; refDistance?: number; rolloffFactor?: number; distanceModel?: string }) => void;
    /** Hide the proximity radius visualization */
    hideProximityRadius: () => void;
}
/** One hotspot folder as getHotspotFolders() reports it. */
export interface HotspotFolderInfo {
    id: string;
    name: string;
    /** The folder it is in (null: at the top). */
    parentId: string | null;
    /** Its own hotspots are shown (not hidden from the directory or by setHotspotFolderVisible). */
    visible: boolean;
    /** Its own hotspots (not those of the folders inside it). */
    hotspotIds: string[];
    /** The extra waypoints filed in it that the directory lists. */
    viewIds: string[];
}

/** The visitor directory as getDirectory() reports it. */
export interface VisitorDirectoryInfo {
    /** The creator turned it on. */
    enabled: boolean;
    /** Its toolbar button is in the viewer (on, lists something, viewer with UI). */
    available: boolean;
    open: boolean;
    title: string;
    /** Hotspots not in a folder, in order. */
    hotspots: Array<{ id: string; title: string }>;
    /**
     * Folders that list something (in them or below), in tree order: a folder, then the folders
     * inside it (`parentId` null at the top). Each has its own hotspots and views.
     */
    folders: Array<{
        id: string;
        name: string;
        parentId: string | null;
        visible: boolean;
        hotspots: Array<{ id: string; title: string }>;
        views: Array<{ id: string; name: string; kind: 'waypoint' | 'extra'; index?: number }>;
    }>;
    /**
     * Its Views not in a folder, in the creator's order: extra waypoints (kind 'extra') and tour
     * waypoints (kind 'waypoint', `index` = their place in the tour, for goToWaypoint).
     */
    views: Array<{ id: string; name: string; kind: 'waypoint' | 'extra'; index?: number }>;
}

/** One region as getRegions() reports it. */
export interface RegionInfo {
    id: string;
    name: string;
    /** #rrggbb */
    color: string;
    /** Switched on (by default, a trigger, the Layers menu or the API). */
    visible: boolean;
    inLayersMenu: boolean;
    /** Dims the rest of the scene while it is on. */
    dimsOutside: boolean;
    section?: string;
    /** Sum of its outline areas, scene units squared. */
    area: number;
}
/** The editor's region overlay (EditorViewerInstance.setRegionEditorOverlay). */
export interface RegionEditorOverlay {
    outlines?: boolean;
    /**
     * The region open in the editor: its areas keep their full colour and every other region's are
     * dimmed, so it is clear whose shape is whose (regionOverlayStyle). Absent: all full colour.
     */
    selectedRegionId?: string | null;
    selectedAreaId?: string | null;
    selectedAreaPoints?: Array<{ x: number; y: number; z: number }> | null;
    draft?: Array<{ x: number; y: number; z: number }> | null;
    draftColor?: string;
    areas?: Array<{ points: Array<{ x: number; y: number; z: number }>; color?: string }>;
    regionOutline?: { points: Array<{ x: number; y: number; z: number }>; color?: string } | null;
}
/**
 * Events emitted by viewer
 */
export type ViewerEvent = 'ready' | 'waypointChange' | 'modeChange' // Emitted when camera mode changes ({ mode: string })
 | 'progressUpdate' // Emitted on scroll progress ({ progress: number, index: number })
 | 'playbackStart' | 'playbackStop' | 'playbackComplete' // Emitted when autoplay reaches end (non-looping tour)
 | 'error' | 'loaded' | 'progress' | 'warning' | 'portalActivated' | 'hotspotClick' // Emitted when a hotspot is clicked ({ hotspot: HotspotData })
 | 'portalClick' // Emitted in editor mode when a portal is clicked ({ portal: PortalData })
 | 'cameraViewMove' // Emitted when a hotspot click or goToExtraWaypoint() starts moving the camera to a view in explore mode ({ exploreMode: 'orbit' | 'fly' })
 | 'xrStart' // Emitted when XR session starts ({ type: 'vr' | 'ar' })
 | 'xrEnd' // Emitted when XR session ends
 | 'overlayShow' | 'overlayHide' // Emitted when an overlay panel appears / disappears ({ overlay: OverlayData })
 | 'photoModeChange' // Emitted when photo mode is entered / left ({ enabled: boolean })
 | 'photoCaptured' // Emitted when a photo is taken and downloaded ({ fileName: string })
 | 'stereoChange' // Emitted when stereo 3D output starts, stops or changes format ({ format: StereoFormat | null })
 | 'frameChange' // 4DGS: emitted when frame changes (frame: number, total: number)
 | 'frameComplete' // 4DGS: emitted when playback reaches end (non-looping)
 | 'splatChange' // Emitted when splat changes (url: string, isOriginal: boolean)
 | 'comparePositionChange' // Emitted when compare slider moves ({ position: number })
 | 'panoramaModeChange' // Emitted when panorama mode changes ({ enabled: boolean })
 | 'regionVisibilityChange' // Emitted when a region is switched on or off ({ id: string, visible: boolean, visibleIds: string[] })
 | 'hotspotFolderVisibilityChange' // Emitted when a hotspot folder is shown or hidden (directory tick box or setHotspotFolderVisible) ({ id: string, visible: boolean, visibleIds: string[] })
 | 'directoryOpenChange' // Emitted when the visitor directory opens or closes ({ open: boolean })
 | 'directorySelect' // Emitted when a visitor picks a hotspot or a view in the directory ({ kind: 'hotspot' | 'view', id: string, waypointIndex?: number }; waypointIndex: the view is a tour waypoint)
 | 'layerVisibilityChange' // Legacy: emitted when layers (Layers-menu and colour-only regions) are shown / hidden ({ visible: string[] })
 | 'measurementSetsChange' // Emitted when measurement sets are shown or hidden (menu or API) ({ visibleIds: string[], sets: MeasurementSetInfo[] })
 | 'entityAnimationStart' // A time-based entity animation began playing from its start ({ id, name, entityId, entityType })
 | 'entityAnimationComplete'; // A 'once' entity animation reached its end ({ id, name, entityId, entityType })
/**
 * Warning event data structure
 */
export interface ViewerWarning {
    type: 'deprecated_format' | 'fallback_used' | 'performance';
    message: string;
    details?: string;
    url?: string;
}
/**
 * Loaded event data structure (includes bandwidth info)
 */
export interface ViewerLoadedData {
    bandwidthUsed: number; // StorySplat-hosted bytes downloaded so far (0 if self-hosted)
    isStorySplatHosted: boolean; // Whether the file is hosted on StorySplat servers
    loadTimeMs?: number; // Time from model load start to ready
    modelUrl?: string; // URL that successfully loaded
    /** True when the viewer runtime reports bandwidth itself (streaming meter).
     *  Consumers must then SKIP their legacy one-shot bandwidth beacon —
     *  the runtime posts the initial report plus periodic streaming deltas. */
    bandwidthSelfReported?: boolean;
}
/**
 * Audio emitter configuration - standalone spatial audio sources
 */
export interface AudioEmitterConfig {
    id: string;
    name?: string;
    /** URL to the audio file */
    url: string;
    /** Position in world space */
    position: {
        x: number;
        y: number;
        z: number;
    };
    /** Volume (0-1) */
    volume?: number;
    /** Loop playback */
    loop?: boolean;
    /** Auto-play when scene loads */
    autoplay?: boolean;
    /** Trigger mode: 'autoplay' (default) plays on load, 'proximity' plays when camera enters range */
    triggerMode?: 'autoplay' | 'proximity';
    /** Stop audio when camera leaves proximity range (proximity mode). Default: true */
    stopOnExit?: boolean;
    /** Distance at which proximity trigger activates. Separate from maxDistance (spatial rolloff). */
    proximityRadius?: number;
    /** Use 3D spatial audio */
    spatialSound?: boolean;
    /** Distance model for spatial audio */
    distanceModel?: 'linear' | 'inverse' | 'exponential';
    /** Maximum distance for spatial audio rolloff (WebAudio panner). NOT the proximity trigger radius. */
    maxDistance?: number;
    /** Reference distance for spatial audio */
    refDistance?: number;
    /** Rolloff factor for spatial audio */
    rolloffFactor?: number;
    /** Whether the emitter is enabled */
    enabled?: boolean;
}

/**
 * Easing type for animation keyframes
 */
export type AnimationEasingType =
  | 'linear'
  | 'step' // hold the value until the next keyframe
  | 'ease-in' | 'ease-out' | 'ease-in-out'
  | 'ease-in-back' | 'ease-out-back' | 'ease-in-out-back'
  | 'ease-out-bounce' | 'ease-out-elastic';

/**
 * Playback mode for independent animations
 */
export type AnimationPlaybackMode = 'once' | 'loop' | 'pingpong';

/**
 * Timeline mode for entity animations
 */
export type AnimationTimelineMode = 'independent' | 'scroll';

/**
 * Entity types that can be animated
 */
export type AnimatableEntityType =
  | 'hotspot' | 'portal' | 'light' | 'particle' | 'audioEmitter' | 'customMesh' | 'mirrorPlane' | 'waterPlane' | 'cloudLayer' | 'htmlMesh'
  // Scene-wide targets (one per scene, fixed ids — see SCENE_ANIMATION_TARGETS)
  | 'fog' | 'weather' | 'wind' | 'focus' | 'haze' | 'postProcessing' | 'relighting' | 'environment' | 'audioReactive'
  // One focus / haze region (entityId = region id)
  | 'focusRegion' | 'hazeRegion'
  // One annotation layer (entityId = layer id)
  | 'annotationLayer'
  // One region (entityId = region id); focusRegion / annotationLayer / focus animations migrate to it
  | 'region';

/**
 * Properties that can be animated on entities
 */
export type AnimatableProperty =
  | 'position.x' | 'position.y' | 'position.z'
  | 'rotation.x' | 'rotation.y' | 'rotation.z'
  | 'scale.x' | 'scale.y' | 'scale.z'
  // Particle system properties
  | 'particle.rate' | 'particle.lifetime' | 'particle.speed' | 'particle.speed2'
  | 'particle.radialSpeed' | 'particle.radialSpeed2'
  | 'particle.scale' | 'particle.scale2'
  | 'particle.rotationSpeedMin' | 'particle.rotationSpeedMax'
  | 'particle.gravity.x' | 'particle.gravity.y' | 'particle.gravity.z'
  | 'particle.opacity'
  // Scene fog (singleton entityType 'fog')
  | 'fog.density' | 'fog.start' | 'fog.end'
  | 'fog.color.r' | 'fog.color.g' | 'fog.color.b'
  // Scene weather (singleton entityType 'weather')
  | 'weather.speed' | 'weather.drift' | 'weather.opacity' | 'weather.elongate'
  | 'weather.particleMinSize' | 'weather.particleMaxSize'
  | 'weather.color.r' | 'weather.color.g' | 'weather.color.b'
  // Scene wind (singleton entityType 'wind'): direction in degrees, speed in scene units / s
  | 'wind.direction' | 'wind.speed' | 'wind.gustiness' | 'wind.turbulence'
  // Any entity: on/off (held, >= 0.5 = shown) and opacity 0-1 (hotspots, portals, 3D models)
  | 'visible' | 'opacity'
  // Lights (cone angle in degrees, spot lights)
  | 'light.color.r' | 'light.color.g' | 'light.color.b'
  | 'light.intensity' | 'light.range' | 'light.coneAngle'
  // Audio emitters (the emitter's Volume setting)
  | 'audio.volume'
  // Sphere hotspots / portals: marker color
  | 'marker.color.r' | 'marker.color.g' | 'marker.color.b'
  // Mirror planes: reflection strength and tint
  | 'mirror.intensity' | 'mirror.tint.r' | 'mirror.tint.g' | 'mirror.tint.b'
  // Water planes
  | 'water.color.r' | 'water.color.g' | 'water.color.b'
  | 'water.deepColor.r' | 'water.deepColor.g' | 'water.deepColor.b'
  | 'water.waveStrength' | 'water.waveSpeed' | 'water.waveScale' | 'water.reflectivity'
  // Cloud layers
  | 'cloud.coverage' | 'cloud.density' | 'cloud.opacity' | 'cloud.windSpeed' | 'cloud.windDirection'
  | 'cloud.evolution' | 'cloud.shadowStrength'
  | 'cloud.color.r' | 'cloud.color.g' | 'cloud.color.b'
  | 'cloud.shadeColor.r' | 'cloud.shadeColor.g' | 'cloud.shadeColor.b'
  // Particles: tint multiplies the system's colour over lifetime (1 = as authored)
  | 'particle.tint.r' | 'particle.tint.g' | 'particle.tint.b'
  // Focus & highlight regions (entityType 'focus', id 'scene.focus')
  | 'focus.tint.r' | 'focus.tint.g' | 'focus.tint.b'
  | 'focus.tintStrength' | 'focus.outsideBrightness' | 'focus.outsideSaturation'
  // Haze regions (entityType 'haze', id 'scene.haze')
  | 'haze.amount' | 'haze.color.r' | 'haze.color.g' | 'haze.color.b'
  // One focus / haze region (entityType 'focusRegion' | 'hazeRegion'): scene coordinates,
  // rotation in degrees, full size, feather 0-1
  | 'region.position.x' | 'region.position.y' | 'region.position.z'
  | 'region.rotation.x' | 'region.rotation.y' | 'region.rotation.z'
  | 'region.size.x' | 'region.size.y' | 'region.size.z'
  | 'region.feather'
  // One focus region's own highlight colour, 0-1 (entityType 'focusRegion' only)
  | 'region.tint.r' | 'region.tint.g' | 'region.tint.b'
  // Annotation layers (entityType 'annotationLayer'): colour 0-1 and strength 0-1
  | 'annotation.color.r' | 'annotation.color.g' | 'annotation.color.b' | 'annotation.opacity'
  // Regions (entityType 'region'): colour 0-1, colour strength 0-1, brightness / saturation of the
  // rest 0-1. A region whose only area is a sphere / box also takes region.position / rotation /
  // size / feather above.
  | 'region.color.r' | 'region.color.g' | 'region.color.b'
  | 'region.colorStrength' | 'region.dimBrightness' | 'region.dimSaturation'
  // Splat relighting ambient (entityType 'relighting', id 'scene.relighting')
  | 'relighting.ambientIntensity' | 'relighting.shadowIntensity'
  | 'relighting.ambientColor.r' | 'relighting.ambientColor.g' | 'relighting.ambientColor.b'
  // Sky & background (entityType 'environment', id 'scene.environment'; skybox rotation in radians)
  | 'skybox.rotation' | 'background.color.r' | 'background.color.g' | 'background.color.b'
  // Audio-reactive splat effect (entityType 'audioReactive', id 'scene.audioReactive')
  | 'audioReactive.master' | 'audioReactive.pulse' | 'audioReactive.wave' | 'audioReactive.wobble'
  | 'audioReactive.hueShift' | 'audioReactive.waveScale' | 'audioReactive.waveSpeed' | 'audioReactive.hueSpeed'
  // Post effects (entityType 'postProcessing', id 'scene.postProcessing')
  | 'post.exposure'
  | 'post.grading.brightness' | 'post.grading.contrast' | 'post.grading.saturation'
  | 'post.grading.tint.r' | 'post.grading.tint.g' | 'post.grading.tint.b'
  | 'post.colorEnhance.shadows' | 'post.colorEnhance.highlights' | 'post.colorEnhance.midtones'
  | 'post.colorEnhance.vibrance' | 'post.colorEnhance.dehaze'
  | 'post.bloom.intensity'
  | 'post.vignette.intensity' | 'post.vignette.inner' | 'post.vignette.outer' | 'post.vignette.curvature'
  | 'post.vignette.color.r' | 'post.vignette.color.g' | 'post.vignette.color.b'
  | 'post.fringing.intensity' | 'post.sharpness';

/**
 * A single keyframe in an animation track
 */
export interface AnimationKeyframeConfig {
    time: number;       // seconds (independent) or 0-100 (scroll)
    value: number;
    easing: AnimationEasingType; // easing FROM this keyframe TO next
}

/**
 * A track animating a single property
 */
export interface AnimationTrackConfig {
    property: AnimatableProperty;
    keyframes: AnimationKeyframeConfig[]; // sorted by time asc
}

/**
 * Entity animation configuration - keyframe animation for scene entities
 */
export interface EntityAnimationConfig {
    id: string;
    name: string;
    entityId: string;
    entityType: AnimatableEntityType;
    timelineMode: AnimationTimelineMode;
    duration: number;        // seconds (independent only)
    playbackMode: AnimationPlaybackMode; // independent only
    autoplay: boolean;       // independent only
    delay: number;           // seconds before start (independent only)
    tracks: AnimationTrackConfig[];
    enabled: boolean;
    /**
     * Start from the beginning each time the tour arrives at this waypoint (0-based index).
     * Independent animations only; such an animation does not autoplay on load.
     */
    triggerWaypoint?: number;
    /**
     * Set on the extra copies a scene-wide Focus & Highlights animation becomes when its focus
     * regions convert to several regions: the original animation's id. Playing, pausing,
     * restarting, resetting or seeking that id acts on every copy.
     */
    sourceId?: string;
}
